import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import LocationScheduleEditor from './LocationScheduleEditor';
import { toLocalDateTimeInput, toLocalDateTimeValue, scheduleEndAfterHour } from '../utils/locationSchedule';
import { extractExpenseReceipt, extractReceipt, mapReceiptToExpense } from '../utils/receiptExtraction';
import { formatTrainingLevel } from '../utils/popupQuestionUtils';
import { cacheOrganizationWidgetKey, getCachedOrganizationWidgetKey } from '../utils/aircraftWidget';
import { appendInstructorWebsiteFields, buildInstructorWidgetSnippet, buildInstructorWebsitePayload, createInstructorWebsiteDraft } from '../utils/instructorWebsite';
import ReceiptExtractionStatus from './ReceiptExtractionStatus';
import PasswordField from './PasswordField';
import { userProfilePayload } from '../utils/userPermissions';
import './UserDialog.css';

const locationAirportCode = location => location?.airportCode || location?.airport || '';

const UserDialog = ({ 
  showDialog, 
  onClose, 
  userData, 
  isReadOnly = false, 
  allowProgressEdit = false,
  onSave = null,
  title = null,
  parentMessage = null,
  organizationId = null,
  style = {}
}) => {
  const { people, roles, aircraft, currentUser, settings, locations, locationWorkSchedules, loadingLocationWorkSchedules } = useApp();
  
  const normalizeReferralRestrictedValue = (value) => {
    if (value === true || value === 1 || value === '1') return '1';
    if (value === false || value === 0 || value === '0') return '0';
    return '1';
  };

  const [formData, setFormData] = useState({
    username: userData?.username || '',
    password: '',
    confirmPassword: '',
    firstName: userData?.firstName || userData?.first_name || '',
    lastName: userData?.lastName || userData?.last_name || '',
    email: userData?.email || '',
    phone: userData?.phone || '',
    address1: userData?.address1 || '',
    address2: userData?.address2 || '',
    city: userData?.city || '',
    state: userData?.state || '',
    zip: userData?.zip || '',
    title: userData?.title || '',
    about: userData?.about || '',
    comment: userData?.comment || '',
    workingOn: userData?.workingOn ?? userData?.pilotLevel ?? userData?.studentLevel ?? '',
    popupQuestionsOptOut: userData?.popupQuestionsOptOut === true || userData?.popupQuestionsOptOut === 1,
    status: userData?.status || 'active',
    roleId: userData?.roleId || '',
    orgRole: userData?.orgRole || '',
    certifications: userData?.certifications || '',
    aircraftList: userData?.aircraftList || [],
    sortOrder: userData?.sortOrder ?? '',
    rate: userData?.rate || '',
    payRate: userData?.payRate || '',
    referredByUserId: userData?.referredByUserId ?? '',
    referralRate: userData?.referralRate ?? '',
    isReferralRestricted: normalizeReferralRestrictedValue(userData?.isReferralRestricted)
  });
  
  const [selectedCertifications, setSelectedCertifications] = useState(
    userData?.certifications ? userData.certifications.split(',').map(certification => certification.trim()).filter(Boolean) : []
  );
  const [activeTab, setActiveTab] = useState('basic');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [referralFilter, setReferralFilter] = useState('');
  const [showReferralDropdown, setShowReferralDropdown] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState({
    sunday: { startTime: '', endTime: '' },
    monday: { startTime: '', endTime: '' },
    tuesday: { startTime: '', endTime: '' },
    wednesday: { startTime: '', endTime: '' },
    thursday: { startTime: '', endTime: '' },
    friday: { startTime: '', endTime: '' },
    saturday: { startTime: '', endTime: '' }
  });
  const [overrides, setOverrides] = useState([]);
  const [newOverride, setNewOverride] = useState({ startDate: '', startTime: '', endDate: '', endTime: '', overrideType: 'off' });
  const [editingOverride, setEditingOverride] = useState(null);
  const [personLessons, setPersonLessons] = useState([]);
  const [personUserInfo, setPersonUserInfo] = useState(null);
  const [workLessons, setWorkLessons] = useState([]);
  const [editingWorkLesson, setEditingWorkLesson] = useState(null);
  const [workLessonMessage, setWorkLessonMessage] = useState('');
  const [activeClients, setActiveClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [expenseMessage, setExpenseMessage] = useState('');
  const [earningsStatements, setEarningsStatements] = useState([]);
  const [editingEarningsId, setEditingEarningsId] = useState(null);
  const [earningsMessage, setEarningsMessage] = useState('');
  const [showEarningsDetailsDialog, setShowEarningsDetailsDialog] = useState(false);
  const [selectedEarningsStatement, setSelectedEarningsStatement] = useState(null);
  const [earningsDetailsActiveTab, setEarningsDetailsActiveTab] = useState('workLessons');
  const [earningsWorkLessons, setEarningsWorkLessons] = useState([]);
  const [earningsExpenses, setEarningsExpenses] = useState([]);
  const [availableRentalHours, setAvailableRentalHours] = useState(0);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceiptImage, setSelectedReceiptImage] = useState('');
  const [selectedReceiptType, setSelectedReceiptType] = useState('');
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [lessonMessage, setLessonMessage] = useState('');
  const [showDeleteWorkLessonModal, setShowDeleteWorkLessonModal] = useState(false);
  const [deleteWorkLessonId, setDeleteWorkLessonId] = useState(null);
  const [earningsDialogTab, setEarningsDialogTab] = useState('lessons');
  const [progressData, setProgressData] = useState([]);
  const [editingProgressId, setEditingProgressId] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(userData?.profileImageUrl || '');
  const [showEnlargedProfileImage, setShowEnlargedProfileImage] = useState(false);
  const [instructorWebsiteUserId, setInstructorWebsiteUserId] = useState(userData?.id ?? null);
  const [instructorWebsite, setInstructorWebsite] = useState(createInstructorWebsiteDraft());
  const [instructorWebsiteLoaded, setInstructorWebsiteLoaded] = useState(false);
  const [instructorWebsiteLoading, setInstructorWebsiteLoading] = useState(false);
  const [instructorWebsiteSaving, setInstructorWebsiteSaving] = useState(false);
  const [instructorWebsiteRetry, setInstructorWebsiteRetry] = useState(0);
  const [instructorWebsiteError, setInstructorWebsiteError] = useState('');
  const [instructorWebsiteImagesChanged, setInstructorWebsiteImagesChanged] = useState(false);
  const [instructorWidgetKey, setInstructorWidgetKey] = useState('');
  const [instructorWidgetLoading, setInstructorWidgetLoading] = useState(false);
  const [instructorWidgetError, setInstructorWidgetError] = useState(null);
  const [instructorWidgetRetry, setInstructorWidgetRetry] = useState(0);
  const [instructorWidgetScope, setInstructorWidgetScope] = useState('all');
  const [instructorWidgetLocationCode, setInstructorWidgetLocationCode] = useState('');
  const [instructorWidgetOverrides, setInstructorWidgetOverrides] = useState({ headingFont: '', headingColor: '', textFont: '', textColor: '', accentColor: '' });
  const [instructorWidgetCopyMessage, setInstructorWidgetCopyMessage] = useState('');
  const [instructorHasWidgetLocationSchedule, setInstructorHasWidgetLocationSchedule] = useState(null);
  const instructorWidgetSnippetRef = React.useRef(null);
  const [progressCategoryFilter, setProgressCategoryFilter] = useState('');
  const [progressStageFilter, setProgressStageFilter] = useState('');
  const [dialogPosition, setDialogPosition] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [newWorkLesson, setNewWorkLesson] = useState({ clientId: '', date: '', startTime: '', clientName: '', dualGiven: '', groundInstruction: '', total: '' });
  const [userExpenses, setUserExpenses] = useState([]);
  const [editingUserExpenseId, setEditingUserExpenseId] = useState(null);
  const [userExpenseMessage, setUserExpenseMessage] = useState('');
  const [extractingUserExpenseIds, setExtractingUserExpenseIds] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  const [documentMessage, setDocumentMessage] = useState('');
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocumentImage, setSelectedDocumentImage] = useState('');
  const [userReservations, setUserReservations] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState('');
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [currentInvoiceLessonId, setCurrentInvoiceLessonId] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [userAgreements, setUserAgreements] = useState([]);
  const [flightLog, setFlightLog] = useState([]);
  const [editingFlightLogId, setEditingFlightLogId] = useState(null);
  const [editingFlightLogData, setEditingFlightLogData] = useState({});
  const [isAddingFlightLog, setIsAddingFlightLog] = useState(false);
  const [newFlightLogData, setNewFlightLogData] = useState({});
  const [popupQuestionAnswers, setPopupQuestionAnswers] = useState([]);
  const [popupQuestionAnswersLoading, setPopupQuestionAnswersLoading] = useState(false);
  const [popupQuestionAnswersMessage, setPopupQuestionAnswersMessage] = useState('');
  const [popupQuestionFeedbackDrafts, setPopupQuestionFeedbackDrafts] = useState({});
  const [savingPopupQuestionFeedbackId, setSavingPopupQuestionFeedbackId] = useState(null);
  const qualificationCertifications = (userData?.certifications || formData.certifications || selectedCertifications.join(',')).split(',').map((certification) => certification.trim().toLowerCase()).filter(Boolean);
  const shouldShowProgressTab = !qualificationCertifications.includes('commercial pilot')
    || !qualificationCertifications.includes('instrument rating')
    || !qualificationCertifications.includes('multi engine rating');
  const [flightLogMessage, setFlightLogMessage] = useState('');
  const [instructorHoursLast24, setInstructorHoursLast24] = useState(null);
  const [isLoadingInstructorHoursLast24, setIsLoadingInstructorHoursLast24] = useState(false);

  const canEditPilotAndSchedule = currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser';
  const canViewFinance = currentUser?.user?.orgRole !== 'staff';
  const canViewTaught = currentUser?.user?.orgRole !== 'staff' && userData?.orgRole === 'staff';
  const canViewTaughtTotals = currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser';
  const canManageReferralFields = currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser';
  const isPilotTabReadOnly = isReadOnly || !canEditPilotAndSchedule;
  const selectedUserRoleId = Number(userData?.roleId ?? userData?.role_id);
  const isSelectedUserInstructor = selectedUserRoleId === 3;
  const currentUserOrgRole = currentUser?.user?.orgRole;
  const canManageInstructorWebsite = ['admin', 'superuser'].includes(currentUserOrgRole);
  const isActiveInstructorDraft = Number(formData.roleId) === 3 && String(formData.status || '').toLowerCase() === 'active';
  const showInstructorWebsiteTab = canManageInstructorWebsite && isActiveInstructorDraft && instructorWebsiteUserId != null;
  const currentUserIsInstructor = String(currentUser?.user?.role || '').toLowerCase() === 'instructor'
    || Number(currentUser?.user?.roleId) === 3
    || [true, 1, '1', 'true'].includes(currentUser?.user?.isInstructor);
  const canEditOwnLocationSchedule = !!userData?.id
    && Number(userData.id) === Number(currentUser?.user?.id ?? currentUser?.id)
    && (currentUserIsInstructor || currentUserOrgRole === 'instructor');
  const isScheduleTabReadOnly = isReadOnly || (!canEditPilotAndSchedule && !canEditOwnLocationSchedule);
  const isPopupQuestionReviewer = currentUserOrgRole === 'instructor'
    || (['admin', 'superuser'].includes(currentUserOrgRole) && currentUserIsInstructor);
  const isViewingOwnPopupQuestionHistory = Number(userData?.id) === Number(currentUser?.user?.id ?? currentUser?.id);
  const canViewPopupQuestionHistory = !!userData?.id && (isViewingOwnPopupQuestionHistory || isPopupQuestionReviewer);
  const canReviewPopupQuestionAnswers = isPopupQuestionReviewer && !isViewingOwnPopupQuestionHistory;
  const instructorWidgetSelectedLocation = locations.find(location => locationAirportCode(location) === instructorWidgetLocationCode);
  const instructorWidgetSnippet = buildInstructorWidgetSnippet({
    widgetKey: instructorWidgetKey,
    scope: instructorWidgetScope,
    instructorId: instructorWebsiteUserId,
    locationCode: instructorWidgetLocationCode,
    locationIsDefault: !!instructorWidgetSelectedLocation?.isDefault,
    overrides: instructorWidgetOverrides
  });

  const handleDialogMouseDown = (event) => {
    if (event.button !== 0) return;
    const bounds = event.currentTarget.parentElement.getBoundingClientRect();
    setDialogPosition({ x: bounds.left, y: bounds.top });
    setDragOffset({ x: event.clientX - bounds.left, y: event.clientY - bounds.top });
    setIsDragging(true);
  };

  React.useEffect(() => {
    if (!isDragging) return undefined;

    const handleMouseMove = (event) => {
      setDialogPosition({ x: event.clientX - dragOffset.x, y: event.clientY - dragOffset.y });
    };
    const handleMouseUp = () => setIsDragging(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  React.useEffect(() => {
    if (!showDialog) return;
    setInstructorWebsiteUserId(userData?.id ?? null);
    setInstructorWebsite(createInstructorWebsiteDraft());
    setInstructorWebsiteLoaded(false);
    setInstructorWebsiteImagesChanged(false);
    setInstructorWebsiteError('');
    setInstructorWidgetScope('all');
    setInstructorWidgetOverrides({ headingFont: '', headingColor: '', textFont: '', textColor: '', accentColor: '' });
  }, [showDialog, userData?.id]);

  React.useEffect(() => {
    if (!showDialog || !showInstructorWebsiteTab || !instructorWebsiteUserId || instructorWebsiteLoaded) return undefined;
    let active = true;
    setInstructorWebsiteLoading(true);
    setInstructorWebsiteError('');
    fetch(`${API_BASE_URL}/instructor/${encodeURIComponent(instructorWebsiteUserId)}/website`, { headers: { 'session-id': currentUser?.sessionId } })
      .then(async response => {
        let data = {};
        try { data = await response.json(); } catch { /* Use the endpoint status fallback below. */ }
        if (!response.ok) {
          const error = new Error(data?.error || `Unable to load instructor website settings (HTTP ${response.status}).`);
          error.status = response.status;
          throw error;
        }
        if (active) {
          setInstructorWebsite(createInstructorWebsiteDraft(data));
          setInstructorWebsiteImagesChanged(false);
          setInstructorWebsiteLoaded(true);
        }
      })
      .catch(error => { if (active) setInstructorWebsiteError(error.status === 503 ? 'Website feature not set up yet (database migration pending).' : error.message); })
      .finally(() => { if (active) setInstructorWebsiteLoading(false); });
    return () => { active = false; };
  }, [showDialog, showInstructorWebsiteTab, instructorWebsiteUserId, instructorWebsiteLoaded, currentUser?.sessionId, instructorWebsiteRetry]);

  React.useEffect(() => {
    if (!showDialog || !showInstructorWebsiteTab) return undefined;
    const organizationId = currentUser?.user?.orgId;
    const cachedKey = getCachedOrganizationWidgetKey(organizationId);
    setInstructorWidgetKey(cachedKey);
    setInstructorWidgetError(null);
    if (cachedKey) return undefined;
    let active = true;
    setInstructorWidgetLoading(true);
    fetch(`${API_BASE_URL}/instructor/widget`, { headers: { 'session-id': currentUser?.sessionId } })
      .then(async response => {
        let data = {};
        try { data = await response.json(); } catch { /* Use the endpoint status fallback below. */ }
        if (!response.ok) {
          const error = new Error(data?.error || `Unable to load instructor widget (HTTP ${response.status}).`);
          error.status = response.status;
          throw error;
        }
        if (!data.widgetKey) throw new Error('The instructor widget API did not return a widget key.');
        cacheOrganizationWidgetKey(organizationId, data.widgetKey);
        if (active) setInstructorWidgetKey(data.widgetKey);
      })
      .catch(error => { if (active) setInstructorWidgetError(error.status === 503 ? 'Website feature not set up yet (database migration pending).' : error.message); })
      .finally(() => { if (active) setInstructorWidgetLoading(false); });
    return () => { active = false; };
  }, [showDialog, showInstructorWebsiteTab, currentUser?.sessionId, currentUser?.user?.orgId, instructorWidgetRetry]);

  React.useEffect(() => {
    if (!showDialog || !showInstructorWebsiteTab || instructorWidgetScope !== 'location' || !instructorWidgetSelectedLocation || !instructorWebsiteUserId) {
      setInstructorHasWidgetLocationSchedule(null);
      return undefined;
    }
    if (instructorWidgetSelectedLocation.isDefault) {
      setInstructorHasWidgetLocationSchedule(true);
      return undefined;
    }
    const cachedSchedules = locationWorkSchedules?.[String(instructorWidgetSelectedLocation.id)];
    if (Array.isArray(cachedSchedules)) {
      setInstructorHasWidgetLocationSchedule(cachedSchedules.some(schedule => Number(schedule.userId) === Number(instructorWebsiteUserId)));
      return undefined;
    }
    if (loadingLocationWorkSchedules) return undefined;
    let active = true;
    setInstructorHasWidgetLocationSchedule(null);
    fetch(`${API_BASE_URL}/work-schedule/${encodeURIComponent(instructorWebsiteUserId)}?locationId=${encodeURIComponent(instructorWidgetSelectedLocation.id)}`, { headers: { 'session-id': currentUser?.sessionId } })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || 'Unable to check instructor availability at this location.');
        if (active) setInstructorHasWidgetLocationSchedule(!!data);
      })
      .catch(() => { if (active) setInstructorHasWidgetLocationSchedule(null); });
    return () => { active = false; };
  }, [showDialog, showInstructorWebsiteTab, instructorWidgetScope, instructorWidgetSelectedLocation, instructorWebsiteUserId, locationWorkSchedules, loadingLocationWorkSchedules, currentUser?.sessionId]);

  const selectInstructorWebsitePhotos = files => {
    const selectedFiles = Array.from(files || []);
    if (!selectedFiles.length) return;
    if (instructorWebsite.images.length + selectedFiles.length > 10) { setInstructorWebsiteError('You can add at most 10 photos.'); return; }
    const invalidFile = selectedFiles.find(file => !['image/png', 'image/jpeg'].includes(file.type) || file.size > 1024 * 1024);
    if (invalidFile) { setInstructorWebsiteError(`${invalidFile.name} must be PNG or JPEG and 1 MB or smaller.`); return; }
    const images = selectedFiles.map(file => ({ file, imageUrl: URL.createObjectURL(file), originalFileName: file.name, originalFileSize: file.size, isNew: true }));
    setInstructorWebsite(previous => ({ ...previous, images: [...previous.images, ...images] }));
    setInstructorWebsiteImagesChanged(true);
    setInstructorWebsiteError('');
  };
  const moveInstructorWebsitePhoto = (index, offset) => setInstructorWebsite(previous => {
    const images = [...previous.images];
    [images[index], images[index + offset]] = [images[index + offset], images[index]];
    return { ...previous, images };
  });
  const removeInstructorWebsitePhoto = index => {
    setInstructorWebsite(previous => {
      const image = previous.images[index];
      if (image?.isNew && image.imageUrl?.startsWith('blob:')) URL.revokeObjectURL(image.imageUrl);
      return { ...previous, images: previous.images.filter((_, imageIndex) => imageIndex !== index) };
    });
    setInstructorWebsiteImagesChanged(true);
  };
  const saveInstructorWebsite = async (userId, draft = instructorWebsite) => {
    const payload = buildInstructorWebsitePayload(draft);
    let body = JSON.stringify(payload);
    const headers = { 'session-id': currentUser?.sessionId, 'Content-Type': 'application/json' };
    if (instructorWebsiteImagesChanged) {
      if (!draft.images.length) throw new Error('Removing every photo is not supported; keep or add at least one photo.');
      const formData = new FormData();
      appendInstructorWebsiteFields(formData, draft);
      for (const image of draft.images) {
        let file = image.file;
        if (!file) {
          const imageResponse = await fetch(image.imageUrl);
          if (!imageResponse.ok) throw new Error(`Unable to reuse photo ${image.originalFileName || ''}.`);
          const blob = await imageResponse.blob();
          file = new File([blob], image.originalFileName || 'instructor-photo.jpg', { type: blob.type || 'image/jpeg' });
        }
        if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 1024 * 1024) throw new Error(`${file.name} must be PNG or JPEG and 1 MB or smaller.`);
        formData.append('websiteImages', file, image.originalFileName || file.name);
      }
      body = formData;
      delete headers['Content-Type'];
    }
    const response = await fetch(`${API_BASE_URL}/instructor/${encodeURIComponent(userId)}/website`, { method: 'POST', headers, body });
    let result = {};
    try { result = await response.json(); } catch { /* Fall through to the HTTP error. */ }
    if (!response.ok) {
      const error = new Error(response.status === 503 ? 'Website feature not set up yet (database migration pending).' : result?.error || `Unable to save instructor website settings (HTTP ${response.status}).`);
      error.status = response.status;
      throw error;
    }
    setInstructorWebsite(createInstructorWebsiteDraft(result));
    setInstructorWebsiteImagesChanged(false);
    setInstructorWebsiteLoaded(true);
    return result;
  };

  const copyInstructorWidgetSnippet = async () => {
    try {
      await navigator.clipboard.writeText(instructorWidgetSnippet);
      setInstructorWidgetCopyMessage('Embed code copied.');
    } catch {
      instructorWidgetSnippetRef.current?.focus();
      instructorWidgetSnippetRef.current?.select();
      setInstructorWidgetCopyMessage('Clipboard unavailable. The embed code is selected for copying.');
    }
  };

  React.useEffect(() => {
    if (!showDialog) {
      setDialogPosition(null);
      setIsDragging(false);
    }
  }, [showDialog, userData?.id]);

  const selectedFormRoleName = (() => {
    if (roles && roles.error) {
      return formData.roleId === '3' ? 'instructor' : '';
    }
    let rolesList = [];
    if (Array.isArray(roles)) {
      rolesList = roles;
    } else if (roles && typeof roles === 'object') {
      rolesList = Object.values(roles);
    }
    const selectedRole = rolesList.find(r => r.id === parseInt(formData.roleId));
    return selectedRole?.name || '';
  })();
  const shouldShowScheduleTab = true;
  const sortedWorkLessons = [...workLessons].sort((leftLesson, rightLesson) => {
    const leftDate = leftLesson?.date ? new Date(`${leftLesson.date}T${leftLesson.startTime || '00:00'}:00`).getTime() : 0;
    const rightDate = rightLesson?.date ? new Date(`${rightLesson.date}T${rightLesson.startTime || '00:00'}:00`).getTime() : 0;

    if (leftDate !== rightDate) {
      return rightDate - leftDate;
    }

    return (rightLesson?.id || 0) - (leftLesson?.id || 0);
  });

  React.useEffect(() => {
    const shouldShowInstructorHours = showDialog && canEditPilotAndSchedule && isSelectedUserInstructor && currentUser?.sessionId;

    if (!shouldShowInstructorHours) {
      setInstructorHoursLast24(null);
      setIsLoadingInstructorHoursLast24(false);
      return;
    }

    let cancelled = false;
    const loadInstructorHoursLast24 = async () => {
      setIsLoadingInstructorHoursLast24(true);
      try {
        const response = await fetch(`${API_BASE_URL}/user/${userData?.id}/lessons/last24/taught`, {
          headers: { 'session-id': currentUser?.sessionId }
        });
        if (!cancelled) {
          if (response.ok) {
            const data = await response.json();
            const hours = Number(data?.hoursTaughtLast24);
            setInstructorHoursLast24(Number.isFinite(hours) ? hours : 0);
          } else {
            setInstructorHoursLast24(0);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setInstructorHoursLast24(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingInstructorHoursLast24(false);
        }
      }
    };

    loadInstructorHoursLast24();

    return () => {
      cancelled = true;
    };
  }, [showDialog, canEditPilotAndSchedule, isSelectedUserInstructor, currentUser?.sessionId, userData?.id]);

  React.useEffect(() => {
    if (userData) {
      const updatedFormData = {
        username: userData.username || '',
        password: '',
        confirmPassword: '',
        firstName: userData.firstName || userData.first_name || '',
        lastName: userData.lastName || userData.last_name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        address1: userData.address1 || '',
        address2: userData.address2 || '',
        city: userData.city || '',
        state: userData.state || '',
        zip: userData.zip || '',
        title: userData.title || '',
        about: userData.about || '',
        comment: userData.comment || '',
        workingOn: userData.workingOn ?? userData.pilotLevel ?? userData.studentLevel ?? '',
        popupQuestionsOptOut: userData.popupQuestionsOptOut === true || userData.popupQuestionsOptOut === 1,
        status: userData.status || 'active',
        roleId: userData.roleId || '',
        orgRole: userData.orgRole || '',
        certifications: userData.certifications || '',
        aircraftList: userData.aircraftList || [],
        sortOrder: userData.sortOrder ?? '',
        rate: userData.rate || '',
        payRate: userData.payRate || '',
        referredByUserId: userData.referredByUserId ?? '',
        referralRate: userData.referralRate ?? '',
        isReferralRestricted: normalizeReferralRestrictedValue(userData.isReferralRestricted)
      };
      setFormData(updatedFormData);
      setSelectedCertifications(userData.certifications ? userData.certifications.split(',').map(certification => certification.trim()).filter(Boolean) : []);
      setProfileImageFile(null);
      setProfileImagePreview(userData.profileImageUrl || '');
      const selectedReferralPerson = (Array.isArray(people) ? people : []).find(person => person.id === updatedFormData.referredByUserId);
      const referralDisplayName = selectedReferralPerson ? `${selectedReferralPerson.firstName || selectedReferralPerson.first_name || ''} ${selectedReferralPerson.lastName || selectedReferralPerson.last_name || ''}`.trim() : '';
      setReferralFilter(referralDisplayName);
      setShowReferralDropdown(false);
      
      // Load schedule for all users so the schedule tab is always available.
      loadUserSchedule();
    }
  }, [userData, roles]);

  const loadFlightLog = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/logbook-entry/user/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) setFlightLog(await response.json());
      else setFlightLog([]);
    } catch { setFlightLog([]); }
  };

  const loadPopupQuestionAnswers = async (userId) => {
    if (!userId || !canViewPopupQuestionHistory) return;
    setPopupQuestionAnswersLoading(true);
    setPopupQuestionAnswersMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/popup-question/answers/user/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || 'Unable to load answered questions');
      }
      const answers = Array.isArray(data) ? data : [];
      setPopupQuestionAnswers(answers);
      setPopupQuestionFeedbackDrafts(Object.fromEntries(answers.map((item) => [item.id, item.instructorFeedback || ''])));
    } catch (error) {
      setPopupQuestionAnswers([]);
      setPopupQuestionAnswersMessage(error.message);
    } finally {
      setPopupQuestionAnswersLoading(false);
    }
  };

  const savePopupQuestionFeedback = async (questionAnswer) => {
    const instructorFeedback = (popupQuestionFeedbackDrafts[questionAnswer.id] ?? '').trim();
    if (!instructorFeedback || savingPopupQuestionFeedbackId != null) return;
    setSavingPopupQuestionFeedbackId(questionAnswer.id);
    setPopupQuestionAnswersMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/popup-question/${questionAnswer.id}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ instructorUserId: currentUser?.user?.id, instructorFeedback })
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || 'Unable to save instructor feedback');
      }
      setPopupQuestionAnswers((previous) => previous.map((item) => item.id === data.id ? data : item));
      setPopupQuestionFeedbackDrafts((previous) => ({ ...previous, [data.id]: data.instructorFeedback || '' }));
      setPopupQuestionAnswersMessage('Instructor feedback saved');
    } catch (error) {
      setPopupQuestionAnswersMessage(error.message);
    } finally {
      setSavingPopupQuestionFeedbackId(null);
    }
  };

  const saveFlightLogEntry = async (isNew) => {
    const raw = isNew ? { ...newFlightLogData, userId: userData?.id } : { ...editingFlightLogData };
    const data = { ...raw, flightDate: raw.flightDate || null };
    try {
      const response = await fetch(`${API_BASE_URL}/logbook-entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        const saved = await response.json();
        if (isNew) {
          setFlightLog(prev => [saved, ...prev]);
          setIsAddingFlightLog(false);
          setNewFlightLogData({});
        } else {
          setFlightLog(prev => prev.map(e => e.id === saved.id ? saved : e));
          setEditingFlightLogId(null);
          setEditingFlightLogData({});
        }
        setFlightLogMessage('Saved successfully');
        setTimeout(() => setFlightLogMessage(''), 3000);
      } else {
        const err = await response.text();
        setFlightLogMessage(`Error: ${err}`);
        setTimeout(() => setFlightLogMessage(''), 5000);
      }
    } catch (error) {
      setFlightLogMessage(`Error: ${error.message}`);
      setTimeout(() => setFlightLogMessage(''), 5000);
    }
  };

  const [showDeleteFlightLogModal, setShowDeleteFlightLogModal] = useState(false);
  const [deleteFlightLogId, setDeleteFlightLogId] = useState(null);

  const deleteFlightLogEntry = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/logbook-entry/${id}`, {
        method: 'DELETE',
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        setFlightLog(prev => prev.filter(e => e.id !== id));
        setFlightLogMessage('Deleted successfully');
        setTimeout(() => setFlightLogMessage(''), 3000);
      }
    } catch (error) {
      setFlightLogMessage(`Error: ${error.message}`);
      setTimeout(() => setFlightLogMessage(''), 5000);
    }
  };

  const loadUserAgreements = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/org-agreement/user/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setUserAgreements(data);
      } else {
        setUserAgreements([]);
      }
    } catch (error) {
      setUserAgreements([]);
    }
  };

  React.useEffect(() => {
    if (activeTab === 'agreements' && userData?.id) {
      loadUserAgreements(userData.id);
    }
    if (activeTab === 'flightLog' && userData?.id) {
      loadFlightLog(userData.id);
    }
    if (activeTab === 'popupQuestions' && canViewPopupQuestionHistory && userData?.id) {
      loadPopupQuestionAnswers(userData.id);
    }
  }, [activeTab, userData?.id]);
  const loadUserSchedule = async () => {
    if (!userData?.id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/work-schedule/${userData.id}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        const convertMilitaryToTime = (militaryTime) => {
          if (!militaryTime) return '';
          // If it's already a string in HH:MM format, return as is
          if (typeof militaryTime === 'string' && militaryTime.includes(':')) {
            return militaryTime;
          }
          // If it's a number, convert from military time
          const hours = Math.floor(militaryTime / 100);
          const minutes = militaryTime % 100;
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        };

        if (data) {
          const schedule = {
            sunday: { startTime: convertMilitaryToTime(data.sunStart), endTime: convertMilitaryToTime(data.sunEnd) },
            monday: { startTime: convertMilitaryToTime(data.monStart), endTime: convertMilitaryToTime(data.monEnd) },
            tuesday: { startTime: convertMilitaryToTime(data.tueStart), endTime: convertMilitaryToTime(data.tueEnd) },
            wednesday: { startTime: convertMilitaryToTime(data.wedStart), endTime: convertMilitaryToTime(data.wedEnd) },
            thursday: { startTime: convertMilitaryToTime(data.thuStart), endTime: convertMilitaryToTime(data.thuEnd) },
            friday: { startTime: convertMilitaryToTime(data.friStart), endTime: convertMilitaryToTime(data.friEnd) },
            saturday: { startTime: convertMilitaryToTime(data.satStart), endTime: convertMilitaryToTime(data.satEnd) }
          };
          
          setWeeklySchedule(schedule);
          
        const mappedOverrides = (data.scheduleOverrides || []).map(override => ({
          ...override,
          startDateTime: toLocalDateTimeInput(override.startDateTime || override.startDatetime),
          endDateTime: toLocalDateTimeInput(override.endDateTime || override.endDatetime)
        }));
          setOverrides(mappedOverrides);
        }
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
    }
  };

  const loadPersonLessons = async (userId) => {
    try {
      const userResponse = await fetch(`${API_BASE_URL}/user/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setPersonUserInfo(userData);
      }
      
      const lessonsResponse = await fetch(`${API_BASE_URL}/lesson/user/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (lessonsResponse.ok) {
        const lessonsData = await lessonsResponse.json();
        setPersonLessons(lessonsData);
      }
    } catch (error) {
      console.error('Error loading person lessons:', error);
    }
  };

  const saveUserSchedule = async (overridesToSave = overrides) => {
    const convertTimeToMilitary = (timeStr) => {
      if (!timeStr) return "";
      return timeStr;
    };

    const payload = {
      userId: userData?.id,
      sunStart: convertTimeToMilitary(weeklySchedule.sunday.startTime),
      sunEnd: convertTimeToMilitary(weeklySchedule.sunday.endTime),
      monStart: convertTimeToMilitary(weeklySchedule.monday.startTime),
      monEnd: convertTimeToMilitary(weeklySchedule.monday.endTime),
      tueStart: convertTimeToMilitary(weeklySchedule.tuesday.startTime),
      tueEnd: convertTimeToMilitary(weeklySchedule.tuesday.endTime),
      wedStart: convertTimeToMilitary(weeklySchedule.wednesday.startTime),
      wedEnd: convertTimeToMilitary(weeklySchedule.wednesday.endTime),
      thuStart: convertTimeToMilitary(weeklySchedule.thursday.startTime),
      thuEnd: convertTimeToMilitary(weeklySchedule.thursday.endTime),
      friStart: convertTimeToMilitary(weeklySchedule.friday.startTime),
      friEnd: convertTimeToMilitary(weeklySchedule.friday.endTime),
      satStart: convertTimeToMilitary(weeklySchedule.saturday.startTime),
      satEnd: convertTimeToMilitary(weeklySchedule.saturday.endTime),
      scheduleOverrides: overridesToSave.map(override => ({
        startDateTime: toLocalDateTimeValue(override.startDateTime),
        endDateTime: toLocalDateTimeValue(override.endDateTime),
        overrideType: override.overrideType
      }))
    };

    try {
      const response = await fetch(`${API_BASE_URL}/work-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage({ text: 'Schedule saved successfully', type: 'success' });
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } else {
        setMessage({ text: 'Failed to save schedule', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Failed to save schedule', type: 'error' });
    }
  };

  const loadProgressData = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/requirement/user/${userId}`, {
        method: 'GET',
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        const records = Array.isArray(data) ? data : [];
        setProgressData(records);
        selectDefaultProgressFilters(records);
      } else {
        setProgressData([]);
        setProgressCategoryFilter('');
        setProgressStageFilter('');
      }
    } catch (error) {
      setProgressData([]);
      setProgressCategoryFilter('');
      setProgressStageFilter('');
    }
  };

  const progressCategoryOrder = ['private pilot', 'instrument rating', 'commercial pilot', 'multi-engine', 'multi-engine rating'];
  const sortProgressCategories = (categories) => categories.slice().sort((a, b) => {
    const aIndex = progressCategoryOrder.indexOf(a.toLowerCase());
    const bIndex = progressCategoryOrder.indexOf(b.toLowerCase());
    if (aIndex !== -1 || bIndex !== -1) {
      return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
    }
    return a.localeCompare(b);
  });

  const getProgressCategories = (records = progressData) => sortProgressCategories([
    ...new Set((Array.isArray(records) ? records : []).map((item) => (item.category || '').trim()).filter(Boolean))
  ]);

  const getProgressItemsForCategory = (category, records = progressData) => {
    if (!category) return Array.isArray(records) ? records : [];
    return (Array.isArray(records) ? records : []).filter((item) => (item.category || '').trim() === category);
  };

  const getProgressStagesForCategory = (category, records = progressData) => [
    ...new Set(getProgressItemsForCategory(category, records).map((item) => (item.stage || '').trim()).filter(Boolean))
  ];

  const getDefaultProgressCategory = (records) => {
    const categories = getProgressCategories(records);
    const normalizeName = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const findCategory = (...names) => categories.find((category) => names.some((name) => normalizeName(category) === normalizeName(name)));
    const certificationValue = userData?.certifications || formData.certifications || selectedCertifications.join(',');
    const certifications = certificationValue.split(',').map((certification) => certification.trim().toLowerCase()).filter(Boolean);
    const hasPrivate = certifications.includes('private pilot');
    const hasInstrument = certifications.includes('instrument rating');
    const hasCommercial = certifications.includes('commercial pilot');
    const hasCfi = certifications.includes('cfi');

    if (hasCommercial && (hasInstrument || hasCfi)) {
      const multiEngine = findCategory('Multi-Engine', 'Multi Engine', 'Multi-Engine Rating', 'Multi Engine Rating')
        || categories.find((category) => normalizeName(category).includes('multiengine'));
      if (multiEngine) return multiEngine;
    }
    if (hasInstrument) {
      const commercial = findCategory('Commercial Pilot');
      if (commercial) return commercial;
    }
    if (hasPrivate) {
      const instrument = findCategory('Instrument Rating');
      if (instrument) return instrument;
    }
    return findCategory('Private Pilot') || categories[0] || '';
  };

  const selectDefaultProgressFilters = (records) => {
    const category = getDefaultProgressCategory(records);
    const stages = getProgressStagesForCategory(category, records);
    setProgressCategoryFilter(category);
    setProgressStageFilter(stages[0] || '');
  };

  const selectProgressCategoryFilter = (category) => {
    const stages = getProgressStagesForCategory(category);
    setProgressCategoryFilter(category);
    setProgressStageFilter(stages[0] || '');
  };

  const progressCategories = getProgressCategories();
  const progressStages = getProgressStagesForCategory(progressCategoryFilter);
  const filteredProgressData = getProgressItemsForCategory(progressCategoryFilter).filter((item) => (
    !progressStageFilter || (item.stage || '').trim() === progressStageFilter
  ));

  const updateLessonField = (lessonId, field, value) => {
    setPersonLessons(prev => prev.map(lesson => 
      lesson.id === lessonId ? { ...lesson, [field]: value } : lesson
    ));
  };

  const saveLesson = async (lessonData) => {
    try {
      const payload = {
        userId: lessonData.userId,
        reservationId: lessonData.reservationId,
        date: lessonData.date,
        aircraftId: lessonData.aircraftId,
        instructorId: lessonData.instructorId,
        startTime: lessonData.startTime,
        endTime: lessonData.endTime,
        flightTime: parseFloat(lessonData.flightTime) || null,
        simulator: parseFloat(lessonData.simulator) || null,
        dualReceived: parseFloat(lessonData.dualReceived) || null,
        ground: parseFloat(lessonData.ground) || null,
        debrief: lessonData.debrief,
        nextFlight: lessonData.nextFlight,
        purchases: parseFloat(lessonData.purchases) || null,
        credit: parseFloat(lessonData.credit) || null,
        fee: parseFloat(lessonData.fee) || 0
      };
      
      // Only include ID if it's not a temporary ID
      if (lessonData.id && !lessonData.id.toString().startsWith('temp_')) {
        payload.id = lessonData.id;
      }
      
      const response = await fetch(`${API_BASE_URL}/lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setLessonMessage('Lesson saved successfully');
        if (userData?.id) loadPersonLessons(userData.id);
        setEditingLessonId(null);
        setTimeout(() => setLessonMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setLessonMessage(`Error: ${errorText}`);
        setTimeout(() => setLessonMessage(''), 5000);
      }
    } catch (error) {
      setLessonMessage(`Error: ${error.message}`);
      setTimeout(() => setLessonMessage(''), 5000);
    }
  };

  const updateProgressField = (itemId, field, value) => {
    setProgressData(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const selectProfileImage = (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setMessage({ text: 'Error: Profile image must be PNG or JPEG', type: 'error' });
      return;
    }
    if (file.size > 1024 * 1024) {
      setMessage({ text: 'Error: Profile image must be 1 MB or smaller', type: 'error' });
      return;
    }
    if (profileImagePreview.startsWith('blob:')) URL.revokeObjectURL(profileImagePreview);
    setProfileImageFile(file);
    setProfileImagePreview(URL.createObjectURL(file));
    setMessage({ text: '', type: '' });
  };

  const saveProgressItem = async (item) => {
    try {
      const payload = [{
        userId: parseInt(userData?.id),
        requirementId: item.id,
        status: item.status,
        level: item.level
      }];
      
      const response = await fetch(`${API_BASE_URL}/requirement/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setProgressMessage('Progress updated successfully');
        setEditingProgressId(null);
        setTimeout(() => setProgressMessage(''), 3000);
      } else {
        setProgressMessage('Error: Failed to update progress');
        setTimeout(() => setProgressMessage(''), 5000);
      }
    } catch (error) {
      setProgressMessage(`Error: ${error.message}`);
      setTimeout(() => setProgressMessage(''), 5000);
    }
  };

  const loadExpenses = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/expense/user/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setUserExpenses(data);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  };

  const updateUserExpenseField = async (expenseId, field, value) => {
    if (field === 'receiptFile' && value) {
      if (value.size > 1024 * 1024) {
        setUserExpenseMessage('Error: File size must be under 1MB');
        setTimeout(() => setUserExpenseMessage(''), 5000);
        return;
      }
    }
    setUserExpenses(prev => prev.map(expense => 
      expense.id === expenseId ? { ...expense, [field]: value } : expense
    ));

    if (field === 'receiptFile' && value) {
      setExtractingUserExpenseIds(prev => [...new Set([...prev, expenseId])]);
      try {
        const extractedFields = await extractExpenseReceipt(value, currentUser?.sessionId, aircraft, settings?.airports);
        if (extractedFields) {
          setUserExpenses(prev => prev.map(expense => (
            expense.id === expenseId && expense.receiptFile === value
              ? { ...expense, ...extractedFields }
              : expense
          )));
        }
      } catch (error) {
        setUserExpenseMessage(`Receipt extraction failed: ${error.message}`);
        setTimeout(() => setUserExpenseMessage(''), 5000);
      } finally {
        setExtractingUserExpenseIds(prev => prev.filter(id => id !== expenseId));
      }
    }
  };

  const handleUserExpenseReceiptChange = async (expenseId, file) => {
    if (!file) return;
    if (file.size > 1048576) {
      updateUserExpenseField(expenseId, 'receiptFile', file);
      return;
    }
    updateUserExpenseField(expenseId, 'receiptFile', file);
    if (!file.type.startsWith('image/')) return;

    setUserExpenseMessage('Scanning receipt...');
    try {
      const result = await extractReceipt(file, currentUser?.sessionId);
      const extractedFields = mapReceiptToExpense(result, aircraft);
      setUserExpenses(prev => prev.map(expense => (
        expense.id === expenseId && expense.receiptFile === file
          ? { ...expense, ...extractedFields }
          : expense
      )));
      setUserExpenseMessage('Receipt scanned. Review the extracted fields before saving.');
    } catch (error) {
      setUserExpenseMessage(`Error: ${error.message}`);
    }
  };

  const saveUserExpense = async (expenseData) => {
    try {
      const formData = new FormData();
      if (expenseData.id && !expenseData.id.toString().startsWith('temp_')) {
        formData.append('id', expenseData.id);
      }
      formData.append('date', expenseData.date);
      formData.append('expense', expenseData.expense);
      formData.append('vendor', expenseData.vendor);
      formData.append('total', parseFloat(expenseData.total) || 0);
      formData.append('userId', userData?.id);
      if (expenseData.aircraftId) {
        formData.append('aircraftId', expenseData.aircraftId);
      }
      if (expenseData.receiptFile) {
        formData.append('receiptImage', expenseData.receiptFile);
      }
      
      const response = await fetch(`${API_BASE_URL}/expense`, {
        method: 'POST',
        headers: { 'session-id': currentUser?.sessionId },
        body: formData
      });
      
      if (response.ok) {
        setUserExpenseMessage('Expense saved successfully');
        loadExpenses(userData?.id);
        setEditingUserExpenseId(null);
        setTimeout(() => setUserExpenseMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setUserExpenseMessage(`Error: ${errorText}`);
        setTimeout(() => setUserExpenseMessage(''), 5000);
      }
    } catch (error) {
      setUserExpenseMessage(`Error: ${error.message}`);
      setTimeout(() => setUserExpenseMessage(''), 5000);
    }
  };

  const addNewUserExpense = () => {
    const tempId = `temp_${Date.now()}`;
    const newExpense = {
      id: tempId,
      date: new Date().toISOString().split('T')[0],
      expense: '',
      vendor: '',
      total: '',
      isAttachment: false,
      userId: userData?.id
    };
    setUserExpenses([newExpense, ...userExpenses]);
    setEditingUserExpenseId(tempId);
  };

  const loadDocuments = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/document/list/user/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const updateDocumentField = (documentId, field, value) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === documentId ? { ...doc, [field]: value } : doc
    ));
  };

  const saveDocument = async (documentData) => {
    try {
      const formData = new FormData();
      if (documentData.id && !documentData.id.toString().startsWith('temp_')) {
        formData.append('id', documentData.id);
      }
      formData.append('userId', userData?.id);
      formData.append('documentType', documentData.documentType);
      formData.append('documentDate', documentData.documentDate);
      if (documentData.expirationDate) {
        formData.append('expirationDate', documentData.expirationDate);
      }
      if (documentData.description) {
        formData.append('description', documentData.description);
      }
      if (documentData.documentFile) {
        formData.append('documentImage', documentData.documentFile);
      }
      
      const response = await fetch(`${API_BASE_URL}/document`, {
        method: 'POST',
        headers: { 'session-id': currentUser?.sessionId },
        body: formData
      });
      
      if (response.ok) {
        setDocumentMessage('Document saved successfully');
        loadDocuments(userData?.id);
        setEditingDocumentId(null);
        setTimeout(() => setDocumentMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setDocumentMessage(`Error: ${errorText}`);
        setTimeout(() => setDocumentMessage(''), 5000);
      }
    } catch (error) {
      setDocumentMessage(`Error: ${error.message}`);
      setTimeout(() => setDocumentMessage(''), 5000);
    }
  };

  const addNewDocument = () => {
    const tempId = `temp_${Date.now()}`;
    const newDocument = {
      id: tempId,
      documentType: '',
      description: '',
      documentDate: new Date().toISOString().split('T')[0],
      expirationDate: '',
      userId: userData?.id
    };
    setDocuments([newDocument, ...documents]);
    setEditingDocumentId(tempId);
  };

  const loadUserReservations = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/reservation/user/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setUserReservations(data);
      } else {
        console.error('Failed to load reservations:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading user reservations:', error);
    }
  };

  const formatTime = (militaryTime) => {
    if (!militaryTime) return '';
    const hours = Math.floor(militaryTime / 100);
    const minutes = militaryTime % 100;
    const h12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return `${h12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const handleCertificationChange = (certification) => {
    setSelectedCertifications(prev => 
      prev.includes(certification) 
        ? prev.filter(c => c !== certification)
        : [...prev, certification]
    );
  };

  const addAircraft = () => {
    setFormData({...formData, aircraftList: [...formData.aircraftList, { aircraftId: '', limitations: '' }]});
  };

  const removeAircraft = (index) => {
    setFormData({...formData, aircraftList: formData.aircraftList.filter((_, i) => i !== index)});
  };

  const updateAircraftList = (index, field, value) => {
    const updated = formData.aircraftList.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setFormData({...formData, aircraftList: updated});
  };

  const loadWorkLessons = async (instructorId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/work-lesson/instructor/${instructorId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkLessons(data);
      }
    } catch (error) {
      console.error('Error loading work lessons:', error);
    }
  };

  const loadAllClients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clients/all`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setAllClients(data);
      }
    } catch (error) {
      console.error('Error loading all clients:', error);
    }
  };

  const getClientName = (clientId, fallbackName = '') => {
    if (!clientId) return fallbackName;
    const client = allClients.find(c => c.id == clientId);
    return client ? `${client.firstName} ${client.lastName}` : fallbackName;
  };

  const saveWorkLesson = async (lessonData) => {
    try {
      const payload = {
        id: lessonData.id,
        instructorId: userData?.id,
        clientId: lessonData.clientId || null,
        date: lessonData.date,
        startTime: lessonData.startTime,
        clientName: lessonData.clientName,
        dualGiven: parseFloat(lessonData.dualGiven) || 0,
        groundInstruction: parseFloat(lessonData.groundInstruction) || 0,
        total: parseFloat(lessonData.total) || 0
      };
      
      const response = await fetch(`${API_BASE_URL}/work-lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setWorkLessonMessage('Lesson saved successfully');
        loadWorkLessons(userData?.id);
        setEditingWorkLesson(null);
        setTimeout(() => setWorkLessonMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setWorkLessonMessage(`Error: ${errorText}`);
        setTimeout(() => setWorkLessonMessage(''), 5000);
      }
    } catch (error) {
      setWorkLessonMessage(`Error: ${error.message}`);
      setTimeout(() => setWorkLessonMessage(''), 5000);
    }
  };

  const deleteWorkLesson = async (lessonId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/work-lesson/${lessonId}`, {
        method: 'DELETE',
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        loadWorkLessons(userData?.id);
      }
    } catch (error) {
      console.error('Error deleting work lesson:', error);
    }
  };

  const loadEarningsStatements = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/earnings-statement/list/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setEarningsStatements(data);
      }
    } catch (error) {
      console.error('Error loading earnings statements:', error);
    }
  };

  const saveEarningsStatement = async (earningsData) => {
    try {
      const payload = {
        startDate: earningsData.startDate,
        endDate: earningsData.endDate,
        userId: userData?.id,
        rentalHoursEarned: parseFloat(earningsData.rentalHoursEarned) || 0,
        rentalHoursUsed: parseFloat(earningsData.rentalHoursUsed) || 0
      };
      
      if (earningsData.id && !earningsData.id.toString().startsWith('temp_')) {
        payload.id = earningsData.id;
      }
      
      const response = await fetch(`${API_BASE_URL}/earnings-statement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setEarningsMessage('Earnings statement saved successfully');
        loadEarningsStatements(userData?.id);
        setEditingEarningsId(null);
        setTimeout(() => setEarningsMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setEarningsMessage(`Error: ${errorText}`);
        setTimeout(() => setEarningsMessage(''), 5000);
      }
    } catch (error) {
      setEarningsMessage(`Error: ${error.message}`);
      setTimeout(() => setEarningsMessage(''), 5000);
    }
  };

  const updateEarningsField = (earningsId, field, value) => {
    setEarningsStatements(prev => prev.map(earnings => 
      earnings.id === earningsId ? { ...earnings, [field]: value } : earnings
    ));
  };

  const addNewEarningsStatement = () => {
    const tempId = `temp_${Date.now()}`;
    const newEarnings = {
      id: tempId,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      userId: userData?.id,
      rentalHoursEarned: '',
      rentalHoursUsed: ''
    };
    setEarningsStatements([newEarnings, ...earningsStatements]);
    setEditingEarningsId(tempId);
  };

  const loadEarningsStatementItems = async (earningsStatementId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/earnings-statement/items/${earningsStatementId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEarningsWorkLessons(data.workLessons || []);
        setEarningsExpenses(data.expenses || []);
        setAvailableRentalHours(data.availableRentalHours || 0);
      }
    } catch (error) {
      console.error('Error loading earnings statement items:', error);
    }
  };

  const handleEarningsStatementClick = (earningsStatement) => {
    setSelectedEarningsStatement(earningsStatement);
    setEarningsDetailsActiveTab('workLessons');
    loadEarningsStatementItems(earningsStatement.id);
    setShowEarningsDetailsDialog(true);
  };

  const handleDialogSave = async () => {
    const dataToSave = userProfilePayload({
      ...formData,
      certifications: selectedCertifications.join(', '),
      aircraftList: formData.aircraftList,
      sortOrder: selectedFormRoleName === 'instructor' ? (formData.sortOrder === '' ? null : parseInt(formData.sortOrder, 10)) : null,
      rate: formData.rate,
      payRate: formData.payRate,
      referredByUserId: formData.referredByUserId === '' ? null : parseInt(formData.referredByUserId, 10),
      referralRate: formData.referralRate === '' ? null : parseFloat(formData.referralRate),
      isReferralRestricted: formData.isReferralRestricted === '' ? 1 : parseInt(normalizeReferralRestrictedValue(formData.isReferralRestricted), 10)
    }, currentUser?.user?.orgRole);
    if (isPilotTabReadOnly) {
      for (const field of ['workingOn', 'pilotLevel', 'certifications', 'aircraftList', 'isInstructor', 'popupQuestionsOptOut']) {
        delete dataToSave[field];
      }
    }
    if (profileImageFile) dataToSave.profileImage = profileImageFile;
    if (userData?.id) dataToSave.id = userData.id;
    if (!dataToSave.password) delete dataToSave.password;
    if (!dataToSave.confirmPassword) delete dataToSave.confirmPassword;
    if (organizationId) dataToSave.organizationId = organizationId;

    const websiteEligible = canManageInstructorWebsite
      && Number(dataToSave.roleId) === 3
      && String(dataToSave.status || '').toLowerCase() === 'active';
    try {
      const userSaveResult = await onSave(dataToSave);
      if (userSaveResult?.success !== true) throw new Error(userSaveResult?.message || 'User save did not complete.');
      const savedUserId = userSaveResult.userId ?? dataToSave.id;
      if (websiteEligible && savedUserId) {
        if (!dataToSave.id) setInstructorWebsiteLoaded(true);
        setInstructorWebsiteUserId(savedUserId);
        setInstructorWebsiteSaving(true);
        try {
          if (dataToSave.id && !instructorWebsiteLoaded) throw new Error('Website settings are not loaded. Retry loading them before saving.');
          await saveInstructorWebsite(savedUserId, instructorWebsiteLoaded ? instructorWebsite : createInstructorWebsiteDraft());
          setMessage({ text: 'User and website settings saved successfully', type: 'success' });
        } catch (error) {
          setInstructorWebsiteError(error.status === 503 ? 'Website feature not set up yet (database migration pending).' : error.message);
          setMessage({ text: 'User saved, but website settings could not be saved.', type: 'success' });
        } finally { setInstructorWebsiteSaving(false); }
      } else {
        setMessage({ text: 'User saved successfully', type: 'success' });
      }
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } catch (error) {
      setMessage({ text: `Error saving user: ${error.message}`, type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  if (!showDialog) return null;

  const personTabGroups = [
    {
      key: 'person', label: 'Person', subtabs: [
        { key: 'basic', label: 'Basic' },
        { key: 'pilot', label: 'Pilot' },
        ...(showInstructorWebsiteTab ? [{ key: 'website', label: 'Website' }] : [])
      ]
    },
    {
      key: 'operations', label: 'Operations', subtabs: [
        ...(shouldShowScheduleTab ? [{ key: 'schedule', label: 'Schedule', onSelect: () => loadUserSchedule() }] : []),
        { key: 'reservations', label: 'Reservations', onSelect: () => { if (userData?.id) loadUserReservations(userData.id); } }
      ]
    },
    {
      key: 'training', label: 'Training', subtabs: [
        { key: 'lessons', label: 'Lessons', onSelect: () => { if (userData?.id) loadPersonLessons(userData.id); } },
        ...(canViewTaught ? [{ key: 'taught', label: 'Taught', onSelect: () => { if (userData?.id) { loadWorkLessons(userData.id); loadAllClients(); } } }] : []),
        ...(shouldShowProgressTab ? [{ key: 'progress', label: 'Progress', onSelect: () => { if (userData?.id) loadProgressData(userData.id); } }] : []),
        ...(canViewPopupQuestionHistory ? [{ key: 'popupQuestions', label: 'Questions' }] : [])
      ]
    },
    {
      key: 'records', label: 'Records', subtabs: [
        { key: 'documents', label: 'Documents', onSelect: () => { if (userData?.id) loadDocuments(userData.id); } },
        ...((currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && userData?.id ? [{ key: 'agreements', label: 'Agreements', onSelect: () => loadUserAgreements(userData.id) }] : []),
        ...(userData?.id ? [{ key: 'flightLog', label: 'Flight Log', onSelect: () => loadFlightLog(userData.id) }] : [])
      ]
    },
    {
      key: 'finance', label: 'Finance', subtabs: [
        ...(userData?.orgRole === 'staff' ? [{ key: 'earnings', label: 'Earnings', onSelect: () => { if (userData?.id) loadEarningsStatements(userData.id); } }] : []),
        ...((currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') ? [{ key: 'expenses', label: 'Expenses', onSelect: () => { if (userData?.id) loadExpenses(userData.id); } }] : [])
      ]
    }
  ].filter(group => group.subtabs.length > 0 && (group.key !== 'finance' || canViewFinance));
  const activePersonTabGroup = personTabGroups.find(group => group.subtabs.some(tab => tab.key === activeTab)) || personTabGroups[0];
  const selectPersonTab = tab => {
    setActiveTab(tab.key);
    tab.onSelect?.();
  };
  const selectPersonTabGroup = group => {
    const tab = group.subtabs.find(item => item.key === activeTab) || group.subtabs[0];
    if (tab) selectPersonTab(tab);
  };

  const dialogTitle = title || (userData?.id ? 
    <><span style={{ color: '#666' }}>Edit Person - </span>{formData.firstName} {formData.lastName}</> : 
    'Add Person'
  );

  return (
    <>
    {showEnlargedProfileImage && profileImagePreview && (
      <div onClick={() => setShowEnlargedProfileImage(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000, cursor: 'zoom-out' }}>
        <div onClick={(event) => event.stopPropagation()} style={{ position: 'relative', backgroundColor: 'white', padding: '1rem', borderRadius: '8px', maxWidth: '92vw', maxHeight: '92vh' }}>
          <button type="button" onClick={() => setShowEnlargedProfileImage(false)} style={{ position: 'absolute', top: '0.25rem', right: '0.25rem', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '18px', zIndex: 1 }}>×</button>
          <img src={profileImagePreview} alt="Profile" style={{ maxWidth: '88vw', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
        </div>
      </div>
    )}
    <div className="person-dialog" data-active-tab={activeTab} style={{
                 position: 'fixed',
                 top: dialogPosition ? `${dialogPosition.y}px` : '50%',
                 left: dialogPosition ? `${dialogPosition.x}px` : '50%',
                 transform: dialogPosition ? 'none' : 'translate(-50%, -50%)',
                 backgroundColor: '#f0f8ff', padding: '0', border: '1px solid #ccc', zIndex: style.zIndex || 1000,
                 width: '60vw', minWidth: 0, maxWidth: '96vw', boxSizing: 'border-box', height: '600px', maxHeight: '96dvh', display: 'flex', flexDirection: 'column', ...style }}>
      <div className="person-dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0', padding: '1rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc' }}>
        <h3 style={{ margin: '0', flex: 1, textAlign: 'center', cursor: 'move', userSelect: 'none' }} onMouseDown={handleDialogMouseDown}>{dialogTitle}</h3>
        <button onClick={() => {
          // Clear all user-specific data
          setPersonLessons([]);
          setWorkLessons([]);
          setExpenses([]);
          setEarningsStatements([]);
          setUserExpenses([]);
          setDocuments([]);
          setProgressData([]);
          setUserReservations([]);
          setWeeklySchedule({
            sunday: { startTime: '', endTime: '' },
            monday: { startTime: '', endTime: '' },
            tuesday: { startTime: '', endTime: '' },
            wednesday: { startTime: '', endTime: '' },
            thursday: { startTime: '', endTime: '' },
            friday: { startTime: '', endTime: '' },
            saturday: { startTime: '', endTime: '' }
          });
          setOverrides([]);
          setActiveTab('basic');
          onClose();
        }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      
      {(parentMessage?.text || message.text) && (
        <div style={{ padding: '0.75rem', margin: '1rem', borderRadius: '4px', backgroundColor: (parentMessage?.type || message.type) === 'success' ? '#d4edda' : '#f8d7da', color: (parentMessage?.type || message.type) === 'success' ? '#155724' : '#721c24', border: `1px solid ${(parentMessage?.type || message.type) === 'success' ? '#c3e6cb' : '#f5c6cb'}` }}>
          {parentMessage?.text || message.text}
        </div>
      )}

      {canEditPilotAndSchedule && isSelectedUserInstructor && (
        <div style={{ padding: '0 1rem 0.75rem 1rem', fontSize: '0.95rem', color: '#333' }}>
          Hours taught in the past 24 hours: {isLoadingInstructorHoursLast24 ? 'Loading...' : (instructorHoursLast24 !== null ? instructorHoursLast24 : 0)}
        </div>
      )}
      
      <div style={{ flexShrink: 0, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', backgroundColor: '#f8f9fa', padding: '0.5rem 1rem 0', borderBottom: '1px solid #dee2e6' }}>
        <div aria-label="Person dialog categories" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
          {personTabGroups.map(group => <button key={group.key} type="button" aria-pressed={activePersonTabGroup.key === group.key} onClick={() => selectPersonTabGroup(group)} style={{ padding: '0.55rem 0.9rem', border: `1px solid ${activePersonTabGroup.key === group.key ? '#2563eb' : 'transparent'}`, borderRadius: '5px 5px 0 0', marginBottom: '-1px', backgroundColor: activePersonTabGroup.key === group.key ? '#2563eb' : '#edf2f7', color: activePersonTabGroup.key === group.key ? 'white' : '#334155', cursor: 'pointer', fontWeight: activePersonTabGroup.key === group.key ? 600 : 500 }}>{group.label}</button>)}
        </div>
        <div aria-label={`${activePersonTabGroup.label} sections`} style={{ display: 'flex', gap: '0.15rem', overflowX: 'auto', paddingTop: '0.5rem' }}>
          {activePersonTabGroup.subtabs.map(tab => <button key={tab.key} type="button" aria-pressed={activeTab === tab.key} onClick={() => selectPersonTab(tab)} style={{ flexShrink: 0, padding: '0.45rem 0.8rem', border: `1px solid ${activeTab === tab.key ? '#cbd5e1' : 'transparent'}`, borderBottom: activeTab === tab.key ? '1px solid white' : '1px solid transparent', borderRadius: '4px 4px 0 0', marginBottom: '-1px', backgroundColor: activeTab === tab.key ? 'white' : 'transparent', color: activeTab === tab.key ? '#1e3a8a' : '#475569', cursor: 'pointer', fontWeight: activeTab === tab.key ? 600 : 500 }}>{tab.label}</button>)}
        </div>
      </div>
      
      <div className="person-dialog-content" style={{ flex: 1, overflow: 'auto', backgroundColor: activeTab === 'basic' ? '#e6f3ff' : activeTab === 'pilot' ? '#f0f8f0' : activeTab === 'schedule' ? '#fff8e1' : activeTab === 'taught' ? '#e8f5e8' : activeTab === 'earnings' ? '#fff8e1' : activeTab === 'expenses' ? '#fff0e6' : activeTab === 'progress' ? '#f0f0f8' : activeTab === 'documents' ? '#f8f0ff' : activeTab === 'reservations' ? '#e6f7ff' : activeTab === 'agreements' ? '#fff8e1' : activeTab === 'flightLog' ? '#e6fff0' : activeTab === 'popupQuestions' ? '#edf6f8' : '#f0f8f0', border: '1px solid #ccc', borderTop: 'none', padding: '2rem', paddingBottom: '0' }}>
        {activeTab === 'basic' && (
          <div className="person-basic-fields">
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Profile Image:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}>
              {profileImagePreview ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <button type="button" onClick={() => setShowEnlargedProfileImage(true)} title="View full-size profile image" style={{ padding: 0, border: 'none', borderRadius: '50%', backgroundColor: 'transparent', cursor: 'zoom-in' }}>
                    <img src={profileImagePreview} alt={`${formData.firstName || formData.username || 'User'}`} style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #bbb', display: 'block' }} />
                  </button>
                  {!isReadOnly && <input type="file" accept="image/png,image/jpeg" title="PNG or JPEG, maximum 1 MB" onChange={(event) => selectProfileImage(event.target.files[0])} style={{ width: '190px', fontSize: '0.8rem' }} />}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: '52px', height: '52px', borderRadius: '50%', backgroundColor: '#f1f3f5', border: '1px solid #bbb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>👤</div>
                  {!isReadOnly && <input type="file" accept="image/png,image/jpeg" title="PNG or JPEG, maximum 1 MB" onChange={(event) => selectProfileImage(event.target.files[0])} style={{ width: '190px', fontSize: '0.8rem' }} />}
                </div>
              )}
            </div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Username:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} required readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Password:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}>
              <div className="person-basic-control" style={{ width: '200px', marginLeft: 0 }}>
                <PasswordField value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} readOnly={isReadOnly} />
              </div>
            </div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Confirm Password:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}>
              <div className="person-basic-control" style={{ width: '200px', marginLeft: 0 }}>
                <PasswordField value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} readOnly={isReadOnly} />
              </div>
            </div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>First Name:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} required readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Last Name:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} required readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Email:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Phone:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Address 1:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.address1} onChange={(e) => setFormData({...formData, address1: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Address 2:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.address2} onChange={(e) => setFormData({...formData, address2: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>City:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>State:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>ZIP:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.zip} onChange={(e) => setFormData({...formData, zip: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Title:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Role:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><select aria-label="Person role" value={formData.roleId} onChange={(e) => setFormData({...formData, roleId: e.target.value})} disabled={isReadOnly || currentUser?.user?.orgRole === 'staff'} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} >
                <option value="">Select Role</option>
                {Array.isArray(roles) ? roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                )) : roles && typeof roles === 'object' ? Object.values(roles).map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                )) : null}
              </select></div>
            {(() => {
              if (roles && roles.error) {
                return formData.roleId === '3';
              }
              let rolesList = [];
              if (Array.isArray(roles)) {
                rolesList = roles;
              } else if (roles && typeof roles === 'object') {
                rolesList = Object.values(roles);
              }
              const selectedRole = rolesList.find(r => r.id === parseInt(formData.roleId));
              return selectedRole?.name === 'instructor';
            })() && (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
              <>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Student Rate ($/hour):</strong></div>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="number" step="0.01" value={formData.rate} onChange={(e) => setFormData({...formData, rate: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Instructor Pay Rate ($/hour):</strong></div>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="number" step="0.01" value={formData.payRate} onChange={(e) => setFormData({...formData, payRate: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
              </>
            )}
            {selectedFormRoleName === 'instructor' && (
              <>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Sort Order:</strong></div>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="number" value={formData.sortOrder} onChange={(e) => setFormData({...formData, sortOrder: e.target.value})} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
              </>
            )}
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Organization Role:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><select aria-label="Person organization role" value={formData.orgRole} onChange={(e) => setFormData({...formData, orgRole: e.target.value})} disabled={isReadOnly || currentUser?.user?.orgRole === 'staff'} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }}>
                <option value="">Select Org Role</option>
                <option value="client">Client</option>
                <option value="member">Member</option>
                <option value="staff">Staff</option>
                {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
                  <option value="admin">Admin</option>
                )}
                {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
                  <option value="superuser">Superuser</option>
                )}
                {currentUser?.user?.orgRole === 'staff' && formData.orgRole === 'admin' && (
                  <option value="admin">Admin</option>
                )}
                {currentUser?.user?.orgRole === 'staff' && formData.orgRole === 'superuser' && (
                  <option value="superuser">Superuser</option>
                )}
              </select></div>
            {canManageReferralFields && (
              <>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Referred By:</strong></div>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}>
                  <div className="person-basic-control" style={{ position: 'relative', width: '200px', marginLeft: 0 }}>
                    <input
                      type="text"
                      placeholder="Type to search users..."
                      value={referralFilter}
                      onChange={(e) => setReferralFilter(e.target.value)}
                      onFocus={() => setShowReferralDropdown(true)}
                      onBlur={() => setTimeout(() => setShowReferralDropdown(false), 200)}
                      readOnly={isReadOnly}
                      style={{ width: '100%', height: '2.25rem', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    {showReferralDropdown && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ccc', borderTop: 'none', maxHeight: '220px', overflowY: 'auto', zIndex: 1000 }}>
                        {(Array.isArray(people) ? people : []).filter(person => {
                          if (referralFilter.length < 2) return true;
                          const firstName = (person.firstName || person.first_name || '').toLowerCase();
                          const lastName = (person.lastName || person.last_name || '').toLowerCase();
                          const filter = referralFilter.toLowerCase();
                          return firstName.includes(filter) || lastName.includes(filter) || (person.username || '').toLowerCase().includes(filter);
                        }).map(person => {
                          const displayName = `${person.firstName || person.first_name || ''} ${person.lastName || person.last_name || ''}`.trim();
                          const label = displayName || person.username || `User ${person.id}`;
                          return (
                            <div
                              key={person.id}
                              style={{ padding: '0.5rem', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                              onMouseDown={() => {
                                setFormData({ ...formData, referredByUserId: person.id });
                                setReferralFilter(label);
                                setShowReferralDropdown(false);
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              {label}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Referral Rate:</strong></div>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}><input type="number" step="0.01" value={formData.referralRate ?? ''} onChange={(e) => setFormData({ ...formData, referralRate: e.target.value })} readOnly={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }} /></div>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Referral Restricted:</strong></div>
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}>
                  <select value={normalizeReferralRestrictedValue(formData.isReferralRestricted)} onChange={(e) => setFormData({ ...formData, isReferralRestricted: e.target.value })} disabled={isReadOnly} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }}>
                    <option value="0">No</option>
                    <option value="1">Yes</option>
                  </select>
                </div>
              </>
            )}
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc' }}><strong>Status:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'flex-start' }}>
              {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && !isReadOnly ? (
                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} style={{ width: '200px', marginLeft: 0, height: '2.25rem' }}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="limited">Limited</option>
                  <option value="unverified">Unverified</option>
                  <option value="verified">Verified</option>
                </select>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}><input type="text" value={formData.status} readOnly style={{ width: '200px', marginLeft: 0, height: '2.25rem', backgroundColor: '#f5f5f5' }} /></div>
              )}
            </div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de', backgroundColor: '#f7fafc', alignSelf: 'start' }}><strong>About:</strong></div>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #d0d7de' }}><textarea value={formData.about} onChange={(e) => setFormData({...formData, about: e.target.value})} rows="5" style={{ width: '100%', resize: 'vertical' }} readOnly={isReadOnly} /></div>
            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f7fafc', alignSelf: 'start' }}><strong>Comment:</strong></div>
            <div style={{ padding: '0.75rem 1rem' }}><textarea value={formData.comment} onChange={(e) => setFormData({...formData, comment: e.target.value})} rows="5" style={{ width: '100%', resize: 'vertical' }} readOnly={isReadOnly} /></div>
          </div>
        )}
        
        {activeTab === 'pilot' && (
          <div>
            {isPilotTabReadOnly && <p role="note">Pilot information and aircraft access are read-only. Only admins and superusers can change them.</p>}
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label htmlFor="pilot-level">Working on:</label>
              <select id="pilot-level" value={formData.workingOn} onChange={(event) => setFormData((previous) => ({ ...previous, workingOn: event.target.value }))} disabled={isPilotTabReadOnly} style={{ minWidth: '190px', padding: '0.4rem', backgroundColor: isPilotTabReadOnly ? '#f5f5f5' : 'white' }}>
                <option value=""></option>
                {['private', 'instrument', 'commercial', 'multi_engine', 'instructor', 'atp'].map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label htmlFor="popup-questions-opt-out">Popup Questions Opt Out:</label>
              <input
                id="popup-questions-opt-out"
                type="checkbox"
                checked={!!formData.popupQuestionsOptOut}
                onChange={(event) => setFormData((previous) => ({ ...previous, popupQuestionsOptOut: event.target.checked }))}
                disabled={isPilotTabReadOnly}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Certifications:</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                {['Student Pilot', 'Private Pilot', 'Instrument Rating', 'Commercial Pilot', 'Multi Engine Rating', 'CFI', 'CFII', 'MEI', 'ATP'].map(cert => (
                  <label key={cert} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <input 
                      type="checkbox" 
                      checked={selectedCertifications.includes(cert)}
                      onChange={() => handleCertificationChange(cert)}
                      disabled={isPilotTabReadOnly}
                    />
                    <span style={{ fontSize: '0.9rem' }}>{cert}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label>Aircraft:</label>
                {!isPilotTabReadOnly && <button type="button" onClick={addAircraft} style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}>Add Aircraft</button>}
              </div>
              {formData.aircraftList.map((ac, index) => (
                <div key={index} className="person-aircraft-row" style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                  <select value={ac.aircraftId} onChange={(e) => updateAircraftList(index, 'aircraftId', e.target.value)} style={{ flex: 1 }} disabled={isPilotTabReadOnly}>
                    <option value="">Select Aircraft</option>
                    {aircraft.map(plane => (
                      <option key={plane.id} value={plane.id}>{plane.make || plane.manufacturer} {plane.model} {plane.tailNumber || plane.registration}</option>
                    ))}
                  </select>
                  <select value={ac.limitations} onChange={(e) => updateAircraftList(index, 'limitations', e.target.value)} style={{ flex: 1 }} disabled={isPilotTabReadOnly}>
                    <option value="">Select Limitation</option>
                    <option value="Solo">Solo</option>
                    <option value="Rental left seat">Rental left seat</option>
                    <option value="Rental">Rental</option>
                    <option value="Instruct">Instruct</option>
                  </select>
                  {!isPilotTabReadOnly && <button type="button" onClick={() => removeAircraft(index)} style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#ff4444', color: 'white' }}>Remove</button>}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'schedule' && locations.length > 0 && <LocationScheduleEditor resourceId={userData?.id} readOnly={isScheduleTabReadOnly} baseRate={userData?.rate} />}
        {activeTab === 'schedule' && locations.length === 0 && (
          <div>
            <div style={{ marginBottom: '2rem' }}>
              <h4>Weekly Schedule</h4>
              {Object.entries(weeklySchedule).map(([day, times]) => (
                <div key={day} className="person-weekly-row" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  <label style={{ width: '100px', textTransform: 'capitalize' }}>{day}:</label>
                  <select value={times.startTime} onChange={(e) => setWeeklySchedule({...weeklySchedule, [day]: {...times, startTime: e.target.value, endTime: times.endTime || scheduleEndAfterHour(e.target.value)}})} disabled={isScheduleTabReadOnly}>
                    <option value="">Select Start Time</option>
                    {Array.from({length: 24}, (_, h) => [
                      `${h.toString().padStart(2, '0')}:00`,
                      `${h.toString().padStart(2, '0')}:30`
                    ]).flat().map(time => {
                      const [hour, minute] = time.split(':');
                      const h12 = hour == 0 ? 12 : hour > 12 ? hour - 12 : hour;
                      const ampm = hour < 12 ? 'AM' : 'PM';
                      const display = `${h12}:${minute} ${ampm}`;
                      return <option key={time} value={time}>{display}</option>;
                    })}
                  </select>
                  <span>to</span>
                  <select value={times.endTime} onChange={(e) => setWeeklySchedule({...weeklySchedule, [day]: {...times, endTime: e.target.value}})} disabled={isScheduleTabReadOnly}>
                    <option value="">Select End Time</option>
                    {Array.from({length: 24}, (_, h) => [
                      `${h.toString().padStart(2, '0')}:00`,
                      `${h.toString().padStart(2, '0')}:30`
                    ]).flat().map(time => {
                      const [hour, minute] = time.split(':');
                      const h12 = hour == 0 ? 12 : hour > 12 ? hour - 12 : hour;
                      const ampm = hour < 12 ? 'AM' : 'PM';
                      const display = `${h12}:${minute} ${ampm}`;
                      return <option key={time} value={time}>{display}</option>;
                    })}
                  </select>
                  {!isScheduleTabReadOnly && <div style={{ display: 'flex', alignItems: 'center', gap: '0.15rem' }}>
                    <button type="button" aria-label={`Copy ${day} hours to other days`} title={`Copy ${day} hours to other days`} onClick={() => setWeeklySchedule(previous => {
                      const source = previous[day];
                      return Object.fromEntries(Object.entries(previous).map(([targetDay, targetTimes]) => [targetDay, targetDay === day ? targetTimes : { ...source }]));
                    })} style={{ border: 0, background: 'transparent', padding: '0.25rem', color: '#526671', fontSize: '18px', cursor: 'pointer' }}>⧉</button>
                    <button type="button" aria-label={`Clear ${day} hours`} title={`Clear ${day} hours`} onClick={() => setWeeklySchedule(previous => ({ ...previous, [day]: { startTime: '', endTime: '' } }))} style={{ border: 0, background: 'transparent', padding: '0.25rem', color: '#a94442', fontSize: '18px', cursor: 'pointer' }}>×</button>
                  </div>}
                </div>
              ))}
              {!isScheduleTabReadOnly && (
                <button onClick={() => saveUserSchedule()} style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Schedule</button>
              )}
            </div>
            
            <div>
              <h4>Schedule Overrides</h4>
              {!isScheduleTabReadOnly && (
                <div className="person-override-fields">
                  <label>Start Date
                  <input type="date" value={newOverride.startDate} onChange={(e) => setNewOverride({...newOverride, startDate: e.target.value, endDate: e.target.value || newOverride.endDate})} />
                  </label>
                  <label>Start Time
                  <select value={newOverride.startTime} onChange={(e) => {
                    const startTime = e.target.value;
                    const end = newOverride.startDate && startTime ? scheduleEndAfterHour(`${newOverride.startDate}T${startTime}`) : '';
                    setNewOverride({ ...newOverride, startTime,
                      ...(!newOverride.endTime ? { endTime: end ? end.slice(11) : scheduleEndAfterHour(startTime), endDate: end ? end.slice(0, 10) : newOverride.endDate } : {})
                    });
                  }}>
                    <option value="">Start Time</option>
                    {Array.from({length: 24}, (_, h) => [
                      `${h.toString().padStart(2, '0')}:00`,
                      `${h.toString().padStart(2, '0')}:30`
                    ]).flat().map(time => {
                      const [hour, minute] = time.split(':');
                      const h12 = hour == 0 ? 12 : hour > 12 ? hour - 12 : hour;
                      const ampm = hour < 12 ? 'AM' : 'PM';
                      const display = `${h12}:${minute} ${ampm}`;
                      return <option key={time} value={time}>{display}</option>;
                    })}
                  </select>
                  </label>
                  <label>End Date
                  <input type="date" value={newOverride.endDate} onChange={(e) => setNewOverride({...newOverride, endDate: e.target.value})} />
                  </label>
                  <label>End Time
                  <select value={newOverride.endTime} onChange={(e) => setNewOverride({...newOverride, endTime: e.target.value})}>
                    <option value="">End Time</option>
                    {Array.from({length: 24}, (_, h) => [
                      `${h.toString().padStart(2, '0')}:00`,
                      `${h.toString().padStart(2, '0')}:30`
                    ]).flat().map(time => {
                      const [hour, minute] = time.split(':');
                      const h12 = hour == 0 ? 12 : hour > 12 ? hour - 12 : hour;
                      const ampm = hour < 12 ? 'AM' : 'PM';
                      const display = `${h12}:${minute} ${ampm}`;
                      return <option key={time} value={time}>{display}</option>;
                    })}
                  </select>
                  </label>
                  <label>Type
                  <select value={newOverride.overrideType} onChange={(e) => setNewOverride({...newOverride, overrideType: e.target.value})}>
                    <option value="on">On</option>
                    <option value="off">Off</option>
                  </select>
                  </label>
                  <button onClick={() => {
                    if (!newOverride.startDate || !newOverride.endDate) return;
                    
                    const startTime = newOverride.startTime || '00:00';
                    const endTime = newOverride.endTime || '23:30';
                    
                    // Create local datetime strings without timezone conversion
                    const startDateTime = `${newOverride.startDate}T${startTime}:00`;
                    const endDateTime = `${newOverride.endDate}T${endTime}:00`;
                    
                    const updatedOverrides = [...overrides, {
                      startDateTime,
                      endDateTime,
                      overrideType: newOverride.overrideType
                    }];
                    setOverrides(updatedOverrides);
                    saveUserSchedule(updatedOverrides);
                    setNewOverride({ startDate: '', startTime: '', endDate: '', endTime: '', overrideType: 'off' });
                  }} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add</button>
                </div>
              )}
              
              {overrides.map((override, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', backgroundColor: '#f8f9fa', marginBottom: '0.25rem', borderRadius: '4px' }}>
                  {editingOverride === index ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.5rem', flex: 1 }}>
                      <input type="date" value={override.startDateTime.split('T')[0]} onChange={(e) => {
                        const updated = overrides.map((o, i) => i === index ? {...o, startDateTime: `${e.target.value}T${o.startDateTime.split('T')[1]}`} : o);
                        setOverrides(updated);
                        saveUserSchedule(updated);
                      }} disabled={isScheduleTabReadOnly} />
                      <select value={override.startDateTime.split('T')[1].slice(0, 5)} onChange={(e) => {
                        const updated = overrides.map((o, i) => i === index ? {...o, startDateTime: `${o.startDateTime.split('T')[0]}T${e.target.value}:00`} : o);
                        setOverrides(updated);
                        saveUserSchedule(updated);
                      }} disabled={isScheduleTabReadOnly}>
                        <option value="">Start Time</option>
                        {Array.from({length: 24}, (_, h) => [
                          `${h.toString().padStart(2, '0')}:00`,
                          `${h.toString().padStart(2, '0')}:30`
                        ]).flat().map(time => <option key={time} value={time}>{time}</option>)}
                      </select>
                      <input type="date" value={override.endDateTime.split('T')[0]} onChange={(e) => {
                        const updated = overrides.map((o, i) => i === index ? {...o, endDateTime: `${e.target.value}T${o.endDateTime.split('T')[1]}`} : o);
                        setOverrides(updated);
                        saveUserSchedule(updated);
                      }} disabled={isScheduleTabReadOnly} />
                      <select value={override.endDateTime.split('T')[1].slice(0, 5)} onChange={(e) => {
                        const updated = overrides.map((o, i) => i === index ? {...o, endDateTime: `${o.endDateTime.split('T')[0]}T${e.target.value}:00`} : o);
                        setOverrides(updated);
                        saveUserSchedule(updated);
                      }} disabled={isScheduleTabReadOnly}>
                        <option value="">End Time</option>
                        {Array.from({length: 24}, (_, h) => [
                          `${h.toString().padStart(2, '0')}:00`,
                          `${h.toString().padStart(2, '0')}:30`
                        ]).flat().map(time => <option key={time} value={time}>{time}</option>)}
                      </select>
                      <select value={override.overrideType} onChange={(e) => {
                        const updated = overrides.map((o, i) => i === index ? {...o, overrideType: e.target.value} : o);
                        setOverrides(updated);
                        saveUserSchedule(updated);
                      }} disabled={isScheduleTabReadOnly}>
                        <option value="on">On</option>
                        <option value="off">Off</option>
                      </select>
                    </div>
                  ) : (
                    <span>{new Date(override.startDateTime).toLocaleDateString()} {new Date(override.startDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {new Date(override.endDateTime).toLocaleDateString()} {new Date(override.endDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} ({override.overrideType})</span>
                  )}
                  {!isScheduleTabReadOnly && (
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      {editingOverride === index ? (
                        <button onClick={() => setEditingOverride(null)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✓</button>
                      ) : (
                        <button onClick={() => setEditingOverride(index)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✏️</button>
                      )}
                      <button onClick={() => {
                        const updatedOverrides = overrides.filter((_, i) => i !== index);
                        setOverrides(updatedOverrides);
                        saveUserSchedule(updatedOverrides);
                      }} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>🗑️</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'website' && showInstructorWebsiteTab && <section aria-label="Instructor website settings" style={{ maxWidth: '960px', margin: '0 auto', padding: '1.25rem', border: '1px solid #cbd9e0', borderRadius: '6px', backgroundColor: '#f3f7f9' }}>
          <h3 style={{ margin: '0 0 1rem' }}>Website</h3>
          {instructorWebsiteLoading && <p role="status">Loading website settings...</p>}
          {instructorWebsiteError && <div role="alert" style={{ color: '#b02a37', marginBottom: '0.75rem' }}>{instructorWebsiteError}{instructorWebsiteUserId && <button type="button" onClick={() => setInstructorWebsiteRetry(value => value + 1)} style={{ marginLeft: '0.5rem' }}>Retry</button>}</div>}
          {instructorWebsiteLoaded && <>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <input type="checkbox" aria-label="Show instructor on website" checked={instructorWebsite.showOnWebsite} onChange={event => setInstructorWebsite(previous => ({ ...previous, showOnWebsite: event.target.checked }))} />
              Show on website
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '1rem 1.25rem', alignItems: 'start' }}>
              <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 120px', gap: '1.25rem', alignItems: 'start' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0, fontWeight: 600 }}>Display name<input aria-label="Website display name" maxLength={100} placeholder={formData.firstName || 'First name'} value={instructorWebsite.displayName} onChange={event => setInstructorWebsite(previous => ({ ...previous, displayName: event.target.value }))} style={{ width: '100%', boxSizing: 'border-box', minHeight: '38px', padding: '0.5rem', font: 'inherit', fontWeight: 400 }} /></label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0, fontWeight: 600 }}>Website order<input aria-label="Website order" type="number" min="1" max="9999" step="1" value={instructorWebsite.sortOrder} onChange={event => setInstructorWebsite(previous => ({ ...previous, sortOrder: event.target.value }))} style={{ width: '100px', maxWidth: '100%', boxSizing: 'border-box', minHeight: '38px', padding: '0.5rem', font: 'inherit', fontWeight: 400 }} /></label>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0, fontWeight: 600, gridColumn: '1 / -1' }}>Price label<input aria-label="Instructor price label" maxLength={100} placeholder="Flight instructor" value={instructorWebsite.priceLabel} onChange={event => setInstructorWebsite(previous => ({ ...previous, priceLabel: event.target.value }))} style={{ width: '100%', boxSizing: 'border-box', minHeight: '38px', padding: '0.5rem', font: 'inherit', fontWeight: 400 }} /><small style={{ fontWeight: 400, lineHeight: 1.5, color: '#526671' }}>Instructors with the same price label are combined into one price line, e.g. 'Flight instructor', 'Senior flight instructor'. Names are not shown.</small></label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0, fontWeight: 600, gridColumn: '1 / -1' }}>Description<textarea aria-label="Instructor website description" maxLength={5000} rows={3} value={instructorWebsite.websiteDescription} onChange={event => setInstructorWebsite(previous => ({ ...previous, websiteDescription: event.target.value }))} style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem', resize: 'vertical', font: 'inherit', fontWeight: 400 }} /></label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0, fontWeight: 600 }}>Highlights<textarea aria-label="Instructor website highlights" rows={5} value={instructorWebsite.highlightsText} onChange={event => setInstructorWebsite(previous => ({ ...previous, highlightsText: event.target.value }))} style={{ width: '100%', boxSizing: 'border-box', minHeight: '144px', padding: '0.5rem', resize: 'vertical', font: 'inherit', fontWeight: 400 }} /><small style={{ fontWeight: 400, lineHeight: 1.5, color: '#526671' }}>Start a line with - for a sub-item, -- for a sub-sub-item.</small></label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0 }}><strong>Highlights preview</strong><ul style={{ margin: 0, minHeight: '144px', boxSizing: 'border-box', padding: '0.5rem 0.75rem 0.5rem 1.75rem', lineHeight: 1.5, overflowWrap: 'anywhere', border: '1px solid #cbd9e0', borderRadius: '4px', backgroundColor: 'white' }}>{instructorWebsite.highlightsText.split(/\r?\n/).filter(line => line.trim()).map((line, index) => {
                const level = Math.min((line.match(/^-+/)?.[0].length || 0), 2);
                return <li key={index} style={{ listStyle: level ? 'circle' : 'disc', marginLeft: `${level}rem` }}>{line.replace(/^-+\s*/, '')}</li>;
              })}</ul></div>
              <div style={{ gridColumn: '1 / -1' }}>
                <h4 style={{ margin: '0 0 0.5rem' }}>Photos ({instructorWebsite.images.length}/10)</h4>
                <p style={{ margin: '0 0 0.5rem', color: '#526671' }}>If no photos are added, the profile picture is used.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {instructorWebsite.images.map((image, index) => <div key={image.id || image.imageUrl} style={{ width: '130px', padding: '0.4rem', border: '1px solid #cbd9e0', borderRadius: '5px', backgroundColor: 'white' }}>
                    <img src={image.imageUrl} alt={image.originalFileName || `Instructor photo ${index + 1}`} style={{ display: 'block', width: '100%', height: '76px', objectFit: 'cover', borderRadius: '3px' }} />
                    <small style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{image.originalFileName || `Photo ${index + 1}`}</small>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                      <div>
                        <button type="button" aria-label={`Move photo ${index + 1} up`} title="Move up" disabled={index === 0} onClick={() => moveInstructorWebsitePhoto(index, -1)}>↑</button>
                        <button type="button" aria-label={`Move photo ${index + 1} down`} title="Move down" disabled={index === instructorWebsite.images.length - 1} onClick={() => moveInstructorWebsitePhoto(index, 1)}>↓</button>
                      </div>
                      <button type="button" aria-label={`Remove photo ${index + 1}`} title={instructorWebsite.images.length === 1 ? 'At least one photo must remain' : 'Remove photo'} disabled={instructorWebsite.images.length <= 1} onClick={() => removeInstructorWebsitePhoto(index)}>×</button>
                    </div>
                  </div>)}
                </div>
                <input aria-label="Add instructor photos" type="file" accept="image/png,image/jpeg" multiple disabled={instructorWebsite.images.length >= 10} onChange={event => { selectInstructorWebsitePhotos(event.target.files); event.target.value = ''; }} style={{ marginTop: '0.75rem' }} />
              </div>
            </div>
            <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #cbd9e0' }}>
              <h4 style={{ margin: '0 0 0.75rem' }}>Website widget</h4>
              {!instructorWebsite.showOnWebsite && <p role="status">This instructor is hidden from the website.</p>}
              {instructorWebsite.images.length === 0 && !profileImagePreview && <p role="status">No photo will be shown.</p>}
              {instructorWidgetLoading && <p role="status">Loading website widget...</p>}
              {instructorWidgetError && <p role="alert" style={{ color: '#b02a37' }}>{instructorWidgetError.status === 503 ? 'Website feature not set up yet (database migration pending).' : instructorWidgetError.message}</p>}
              <fieldset disabled={!instructorWidgetKey} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', alignItems: 'center', border: 0, padding: 0, margin: '0.75rem 0' }}>
                <legend>Show</legend>
                <label><input type="radio" name="instructor-widget-scope" value="all" checked={instructorWidgetScope === 'all'} onChange={() => setInstructorWidgetScope('all')} /> All website instructors</label>
                <label><input type="radio" name="instructor-widget-scope" value="instructor" checked={instructorWidgetScope === 'instructor'} onChange={() => setInstructorWidgetScope('instructor')} /> Only this instructor</label>
                <label><input type="radio" name="instructor-widget-scope" value="location" checked={instructorWidgetScope === 'location'} onChange={() => setInstructorWidgetScope('location')} /> Only instructors at a location</label>
                {instructorWidgetScope === 'location' && <select aria-label="Instructor widget location" value={instructorWidgetLocationCode} onChange={event => setInstructorWidgetLocationCode(event.target.value)}><option value="">Select airport</option>{locations.filter(location => locationAirportCode(location)).map(location => <option key={location.id} value={locationAirportCode(location)}>{locationAirportCode(location)}{location.name ? ` - ${location.name}` : ''}</option>)}</select>}
              </fieldset>
              {instructorWidgetScope === 'location' && instructorWidgetSelectedLocation?.isDefault && <p>Default location shows everyone.</p>}
              {instructorWidgetScope === 'location' && instructorWidgetSelectedLocation && !instructorWidgetSelectedLocation.isDefault && instructorHasWidgetLocationSchedule === false && <p role="status">This instructor won't appear for this location.</p>}
              <label>Embed code<textarea ref={instructorWidgetSnippetRef} aria-label="Instructor widget embed code" readOnly rows={2} value={instructorWidgetSnippet} style={{ width: '100%', fontFamily: 'monospace' }} /></label>
              <button type="button" disabled={!instructorWidgetKey || (instructorWidgetScope === 'location' && !instructorWidgetLocationCode)} onClick={copyInstructorWidgetSnippet}>Copy</button>
              {instructorWidgetCopyMessage && <span role="status" style={{ marginLeft: '0.5rem' }}>{instructorWidgetCopyMessage}</span>}
              <p>Paste where the instructor list should appear. It picks up the website's fonts and colours automatically.</p>
              <p>Only active instructors with 'Show on website' turned on are listed. Changes appear within about a minute.</p>
              <details><summary>Styling overrides (advanced)</summary><div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem 1rem', marginTop: '0.75rem' }}>{Object.entries({ headingFont: 'Heading font', headingColor: 'Heading color', textFont: 'Text font', textColor: 'Text color', accentColor: 'Accent color' }).map(([key, label]) => <label key={key}>{label}<input aria-label={`Instructor widget ${label}`} value={instructorWidgetOverrides[key]} onChange={event => setInstructorWidgetOverrides(previous => ({ ...previous, [key]: event.target.value }))} /></label>)}</div></details>
            </div>
          </>}
        </section>}

        {false && activeTab === 'website' && showInstructorWebsiteTab && <section aria-label="Instructor website settings" style={{ maxWidth: '900px', margin: '0 auto', padding: '1.25rem', border: '1px solid #cbd9e0', borderRadius: '6px', backgroundColor: '#f3f7f9' }}>
          <h3 style={{ margin: '0 0 1rem' }}>Website</h3>
          {instructorWebsiteLoading && <p role="status">Loading website settings...</p>}
          {instructorWebsiteError && <p role="alert" style={{ color: '#b02a37' }}>{instructorWebsiteError}</p>}
          {instructorWebsiteLoaded && <>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <input type="checkbox" aria-label="Show instructor on website" checked={instructorWebsite.showOnWebsite} onChange={event => setInstructorWebsite(previous => ({ ...previous, showOnWebsite: event.target.checked }))} />
              Show on website
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem 1rem', alignItems: 'start' }}>
              <label>Display name<input aria-label="Website display name" maxLength={100} placeholder={formData.firstName || 'First name'} value={instructorWebsite.displayName} onChange={event => setInstructorWebsite(previous => ({ ...previous, displayName: event.target.value }))} /></label>
              <label>Website order<input aria-label="Website order" type="number" min="1" max="9999" step="1" value={instructorWebsite.sortOrder} onChange={event => setInstructorWebsite(previous => ({ ...previous, sortOrder: event.target.value }))} /></label>
              <label style={{ gridColumn: '1 / -1' }}>Description<textarea aria-label="Instructor website description" maxLength={5000} rows={3} value={instructorWebsite.websiteDescription} onChange={event => setInstructorWebsite(previous => ({ ...previous, websiteDescription: event.target.value }))} /></label>
              <label>Highlights<textarea aria-label="Instructor website highlights" rows={5} value={instructorWebsite.highlightsText} onChange={event => setInstructorWebsite(previous => ({ ...previous, highlightsText: event.target.value }))} /></label>
              <div>
                <strong>Highlights preview</strong>
                <ul style={{ margin: '0.4rem 0', paddingLeft: '1.25rem' }}>{instructorWebsite.highlightsText.split(/\r?\n/).filter(line => line.trim()).map((line, index) => {
                  const level = Math.min((line.match(/^-+/)?.[0].length || 0), 2);
                  return <li key={index} style={{ listStyle: level ? 'circle' : 'disc', marginLeft: `${level * 1}rem` }}>{line.replace(/^-+\s*/, '')}</li>;
                })}</ul>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <h4 style={{ margin: '0 0 0.5rem' }}>Photos ({instructorWebsite.images.length}/10)</h4>
                <p style={{ margin: '0 0 0.5rem', color: '#526671' }}>If no photos are added, the profile picture is used.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {instructorWebsite.images.map((image, index) => <div key={image.id || image.imageUrl} style={{ width: '130px', padding: '0.4rem', border: '1px solid #cbd9e0', borderRadius: '5px', backgroundColor: 'white' }}>
                    <img src={image.imageUrl} alt={image.originalFileName || `Instructor photo ${index + 1}`} style={{ display: 'block', width: '100%', height: '76px', objectFit: 'cover', borderRadius: '3px' }} />
                    <small style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{image.originalFileName || `Photo ${index + 1}`}</small>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                      <div>
                        <button type="button" aria-label={`Move photo ${index + 1} up`} title="Move up" disabled={index === 0} onClick={() => moveInstructorWebsitePhoto(index, -1)}>↑</button>
                        <button type="button" aria-label={`Move photo ${index + 1} down`} title="Move down" disabled={index === instructorWebsite.images.length - 1} onClick={() => moveInstructorWebsitePhoto(index, 1)}>↓</button>
                      </div>
                      <button type="button" aria-label={`Remove photo ${index + 1}`} title={instructorWebsite.images.length === 1 ? 'At least one photo must remain' : 'Remove photo'} disabled={instructorWebsite.images.length <= 1} onClick={() => removeInstructorWebsitePhoto(index)}>×</button>
                    </div>
                  </div>)}
                </div>
                <input aria-label="Add instructor photos" type="file" accept="image/png,image/jpeg" multiple disabled={instructorWebsite.images.length >= 10} onChange={event => { selectInstructorWebsitePhotos(event.target.files); event.target.value = ''; }} style={{ marginTop: '0.75rem' }} />
              </div>
            </div>
            <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #cbd9e0' }}>
              <h4 style={{ margin: '0 0 0.75rem' }}>Website widget</h4>
              {!instructorWebsite.showOnWebsite && <p role="status">This instructor is hidden from the website.</p>}
              {instructorWebsite.images.length === 0 && !profileImagePreview && <p role="status">No photo will be shown.</p>}
              {instructorWidgetLoading && <p role="status">Loading website widget...</p>}
              {instructorWidgetError && <p role="alert" style={{ color: '#b02a37' }}>{instructorWidgetError.status === 503 ? 'Website feature not set up yet (database migration pending).' : instructorWidgetError.message}</p>}
              <fieldset disabled={!instructorWidgetKey} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', alignItems: 'center', border: 0, padding: 0, margin: '0.75rem 0' }}>
                <legend>Show</legend>
                <label><input type="radio" name="instructor-widget-scope" value="all" checked={instructorWidgetScope === 'all'} onChange={() => setInstructorWidgetScope('all')} /> All website instructors</label>
                <label><input type="radio" name="instructor-widget-scope" value="instructor" checked={instructorWidgetScope === 'instructor'} onChange={() => setInstructorWidgetScope('instructor')} /> Only this instructor</label>
                <label><input type="radio" name="instructor-widget-scope" value="location" checked={instructorWidgetScope === 'location'} onChange={() => setInstructorWidgetScope('location')} /> Only instructors at a location</label>
                {instructorWidgetScope === 'location' && <select aria-label="Instructor widget location" value={instructorWidgetLocationCode} onChange={event => setInstructorWidgetLocationCode(event.target.value)}>
                  <option value="">Select airport</option>
                  {locations.filter(location => locationAirportCode(location)).map(location => <option key={location.id} value={locationAirportCode(location)}>{locationAirportCode(location)}{location.name ? ` - ${location.name}` : ''}</option>)}
                </select>}
              </fieldset>
              {instructorWidgetScope === 'location' && instructorWidgetSelectedLocation?.isDefault && <p>Default location shows all instructors.</p>}
              {instructorWidgetScope === 'location' && instructorWidgetSelectedLocation && !instructorWidgetSelectedLocation.isDefault && instructorHasWidgetLocationSchedule === false && <p role="status">This instructor won't appear for this location.</p>}
              <label>Embed code<textarea ref={instructorWidgetSnippetRef} aria-label="Instructor widget embed code" readOnly rows={2} value={instructorWidgetSnippet} style={{ width: '100%', fontFamily: 'monospace' }} /></label>
              <button type="button" disabled={!instructorWidgetKey || (instructorWidgetScope === 'instructor' && !instructorWebsiteUserId) || (instructorWidgetScope === 'location' && !instructorWidgetLocationCode)} onClick={copyInstructorWidgetSnippet}>Copy</button>
              {instructorWidgetCopyMessage && <span role="status" style={{ marginLeft: '0.5rem' }}>{instructorWidgetCopyMessage}</span>}
              <p>Paste where the instructor list should appear. It picks up the website's fonts and colours automatically.</p>
              <p>Only active instructors with 'Show on website' turned on are listed. Changes appear within about a minute.</p>
              <details><summary>Styling overrides (advanced)</summary>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem 1rem', marginTop: '0.75rem' }}>
                  {Object.entries({ headingFont: 'Heading font', headingColor: 'Heading color', textFont: 'Text font', textColor: 'Text color', accentColor: 'Accent color' }).map(([key, label]) => <label key={key}>{label}<input aria-label={`Instructor widget ${label}`} value={instructorWidgetOverrides[key]} onChange={event => setInstructorWidgetOverrides(previous => ({ ...previous, [key]: event.target.value }))} /></label>)}
                </div>
              </details>
            </div>
          </>}
        </section>}
        
        {activeTab === 'lessons' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4>Lessons</h4>
              {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && !isReadOnly && (
                <button onClick={() => {
                  const tempId = `temp_${Date.now()}`;
                  const newLesson = {
                    id: tempId,
                    userId: userData?.id,
                    date: new Date().toISOString().split('T')[0],
                    flightTime: '',
                    simulator: '',
                    dualReceived: '',
                    ground: '',
                    purchases: '',
                    fee: '',
                    credit: '',
                    debrief: '',
                    nextFlight: ''
                  };
                  setPersonLessons([newLesson, ...personLessons]);
                  setEditingLessonId(tempId);
                }} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add New Lesson</button>
              )}
            </div>
            {lessonMessage && (
              <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: lessonMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: lessonMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${lessonMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                {lessonMessage}
              </div>
            )}
            {personLessons.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666' }}>No lessons found</div>
            ) : (
              <div>
                <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                  <strong>Balance: ${personUserInfo?.balance || 0}</strong>
                </div>
                <div style={{ overflow: 'auto' }}>
                  <table style={{ minWidth: '1200px', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem', tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && !isReadOnly && <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '48px' }}>Actions</th>}
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Instructor</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Flight Time</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Simulator</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Dual Received</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Ground</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Purchases</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Fee</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Payment</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Credit</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '300px' }}>Debrief</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '300px' }}>Next Flight</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personLessons.map((lesson, index) => {
                        const isEditing = editingLessonId === lesson.id;
                        return (
                          <tr key={index}>
                            {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && !isReadOnly && (
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  {isEditing ? (
                                    <>
                                      <button onClick={() => saveLesson(lesson)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                      <button onClick={() => {
                                        if (lesson.id && lesson.id.toString().startsWith('temp_')) {
                                          setPersonLessons(prev => prev.filter(l => l.id !== lesson.id));
                                        }
                                        setEditingLessonId(null);
                                      }} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                    </>
                                  ) : (
                                    <>
                                      <button onClick={() => setEditingLessonId(lesson.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }} title="Edit Lesson">✏️</button>
                                      {!lesson.id.toString().startsWith('temp_') && (
                                        <button onClick={async () => {
                                          setIsLoadingInvoice(true);
                                          try {
                                            const response = await fetch(`${API_BASE_URL}/lesson/${lesson.id}/invoice`, {
                                              method: 'POST',
                                              headers: { 'session-id': currentUser?.sessionId }
                                            });
                                            if (response.ok) {
                                              const blob = await response.blob();
                                              const url = URL.createObjectURL(blob);
                                              setInvoicePdfUrl(url);
                                              setCurrentInvoiceLessonId(lesson.id);
                                              setShowInvoiceModal(true);
                                            }
                                          } catch (error) {
                                            console.error('Error loading invoice:', error);
                                          } finally {
                                            setIsLoadingInvoice(false);
                                          }
                                        }} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }} title="View Invoice">🧾</button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? <input type="date" value={lesson.date || ''} onChange={(e) => updateLessonField(lesson.id, 'date', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : '')}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? (
                                <select value={lesson.instructorId || ''} onChange={(e) => updateLessonField(lesson.id, 'instructorId', e.target.value)} style={{ width: '100%', border: 'none' }}>
                                  <option value="">None</option>
                                  {people && people.filter(p => p.roleId === 3 || (Array.isArray(roles) ? roles : Object.values(roles || {})).find(r => r.id === parseInt(p.roleId))?.name === 'instructor').map(p => (
                                    <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                                  ))}
                                </select>
                              ) : (
                                (() => { const inst = people && people.find(p => p.id === lesson.instructorId); return inst ? inst.firstName : ''; })()
                              )}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? <input type="number" step="0.1" value={lesson.flightTime || ''} onChange={(e) => updateLessonField(lesson.id, 'flightTime', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.flightTime || 0)}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? <input type="number" step="0.1" value={lesson.simulator || ''} onChange={(e) => updateLessonField(lesson.id, 'simulator', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.simulator || 0)}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? <input type="number" step="0.1" value={lesson.dualReceived || ''} onChange={(e) => updateLessonField(lesson.id, 'dualReceived', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.dualReceived || 0)}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? <input type="number" step="0.1" value={lesson.ground || ''} onChange={(e) => updateLessonField(lesson.id, 'ground', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.ground || 0)}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? <input type="number" step="0.01" value={lesson.purchases || ''} onChange={(e) => updateLessonField(lesson.id, 'purchases', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${lesson.purchases || 0}`}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? <input type="number" step="0.01" value={lesson.fee || ''} onChange={(e) => updateLessonField(lesson.id, 'fee', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${lesson.fee || 0}`}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? <input type="number" step="0.01" value={lesson.payment || ''} onChange={(e) => updateLessonField(lesson.id, 'payment', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${lesson.payment || 0}`}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {isEditing ? <input type="number" step="0.01" value={lesson.credit || ''} onChange={(e) => updateLessonField(lesson.id, 'credit', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${lesson.credit || 0}`}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem', width: '300px', wordWrap: 'break-word' }}>
                              {isEditing ? <textarea value={lesson.debrief || ''} onChange={(e) => updateLessonField(lesson.id, 'debrief', e.target.value)} style={{ width: '100%', border: 'none', resize: 'vertical', minHeight: '60px' }} /> : (lesson.debrief || '')}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem', width: '300px', wordWrap: 'break-word' }}>
                              {isEditing ? <textarea value={lesson.nextFlight || ''} onChange={(e) => updateLessonField(lesson.id, 'nextFlight', e.target.value)} style={{ width: '100%', border: 'none', resize: 'vertical', minHeight: '60px' }} /> : (lesson.nextFlight || '')}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              ${lesson.balance || 0}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                        {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && !isReadOnly && <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>Totals:</td>}
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}></td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}></td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{personLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.flightTime) || 0), 0).toFixed(1)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{personLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.simulator) || 0), 0).toFixed(1)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{personLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.dualReceived) || 0), 0).toFixed(1)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{personLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.ground) || 0), 0).toFixed(1)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>${personLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.purchases) || 0), 0).toFixed(2)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>${personLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.fee) || 0), 0).toFixed(2)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>${personLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.payment) || 0), 0).toFixed(2)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>${personLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.credit) || 0), 0).toFixed(2)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}></td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}></td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        
        {canViewTaught && activeTab === 'taught' && (
          <div>
            <h4>Work Lessons Taught</h4>
            {workLessonMessage && (
              <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: workLessonMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: workLessonMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${workLessonMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                {workLessonMessage}
              </div>
            )}
            {workLessons.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666' }}>No work lessons found</div>
            ) : (
                <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      {canViewTaughtTotals && <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '60px' }}>#</th>}
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Client</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Dual Given</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Ground</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedWorkLessons.map((lesson, index) => (
                      <tr key={index}>
                        {canViewTaughtTotals && <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{sortedWorkLessons.length - index}</td>}
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : ''}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{getClientName(lesson.clientId, lesson.clientName)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.dualGiven || 0}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.groundInstruction || 0}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.total || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                  {canViewTaughtTotals && (
                    <tfoot>
                      <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                        <td colSpan="3" style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>Totals:</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{workLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.dualGiven) || 0), 0).toFixed(1)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{workLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.groundInstruction) || 0), 0).toFixed(1)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{workLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.total) || 0), 0).toFixed(1)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
                </div>
            )}
          </div>
        )}
        
        {canViewFinance && activeTab === 'earnings' && (
          <div>
            <h4>Earnings Statements</h4>
            {earningsMessage && (
              <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: earningsMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: earningsMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${earningsMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                {earningsMessage}
              </div>
            )}
            <div style={{ marginBottom: '1rem' }}>
              <button onClick={addNewEarningsStatement} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add New Statement</button>
            </div>
            {earningsStatements.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666' }}>No earnings statements found</div>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '14.3%' }}>Actions</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '14.3%' }}>Start Date</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '14.3%' }}>End Date</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '14.3%' }}>Total</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '14.3%' }}>Expenses</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '14.3%' }}>Rental Hours Earned</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '14.3%' }}>Rental Hours Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earningsStatements.map((earnings, index) => {
                      const isEditing = editingEarningsId === earnings.id;
                      return (
                        <tr key={index} onClick={() => !isEditing && handleEarningsStatementClick(earnings)} style={{ cursor: !isEditing ? 'pointer' : 'default' }}>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              {isEditing ? (
                                <>
                                  <button onClick={() => saveEarningsStatement(earnings)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                  <button onClick={() => setEditingEarningsId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                </>
                              ) : (
                                <button onClick={() => setEditingEarningsId(earnings.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                              )}
                            </div>
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="date" value={earnings.startDate} onChange={(e) => updateEarningsField(earnings.id, 'startDate', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (earnings.startDate ? new Date(earnings.startDate + 'T00:00:00').toLocaleDateString() : '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="date" value={earnings.endDate} onChange={(e) => updateEarningsField(earnings.id, 'endDate', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (earnings.endDate ? new Date(earnings.endDate + 'T00:00:00').toLocaleDateString() : '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            ${earnings.total || 0}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            ${earnings.expenses || 0}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing && !isReadOnly ? <input type="number" step="0.1" value={earnings.rentalHoursEarned} onChange={(e) => updateEarningsField(earnings.id, 'rentalHoursEarned', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (earnings.rentalHoursEarned || 0)}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing && !isReadOnly ? <input type="number" step="0.1" value={earnings.rentalHoursUsed} onChange={(e) => updateEarningsField(earnings.id, 'rentalHoursUsed', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (earnings.rentalHoursUsed || 0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {canViewFinance && activeTab === 'expenses' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4>Expenses</h4>
              {!isReadOnly && <button onClick={addNewUserExpense} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add New</button>}
            </div>
            {userExpenseMessage && (
              <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: userExpenseMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: userExpenseMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${userExpenseMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                {userExpenseMessage}
              </div>
            )}
            <div style={{ overflow: 'auto', maxHeight: '400px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                    {!isReadOnly && <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Actions</th>}
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Expense</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Vendor</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Aircraft</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {userExpenses.map((expense, index) => {
                    const isEditing = editingUserExpenseId === expense.id;
                    const expenseAircraft = aircraft.find(a => a.id === expense.aircraftId);
                    return (
                      <tr key={expense.id || index}>
                        {!isReadOnly && (
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              {isEditing ? (
                                <>
                                  <button onClick={() => saveUserExpense(expense)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                  <button onClick={() => setEditingUserExpenseId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                </>
                              ) : (
                                <button onClick={() => setEditingUserExpenseId(expense.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                              )}
                            </div>
                          </td>
                        )}
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? <input type="date" value={expense.date || ''} onChange={(e) => updateUserExpenseField(expense.id, 'date', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.date ? new Date(expense.date + 'T00:00:00').toLocaleDateString() : '')}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? <input type="text" value={expense.expense || ''} onChange={(e) => updateUserExpenseField(expense.id, 'expense', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.expense || '')}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? <input type="text" value={expense.vendor || ''} onChange={(e) => updateUserExpenseField(expense.id, 'vendor', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.vendor || '')}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? (
                            <select value={expense.aircraftId || ''} onChange={(e) => updateUserExpenseField(expense.id, 'aircraftId', e.target.value)} style={{ width: '100%', border: 'none' }}>
                              <option value="">Select Aircraft</option>
                              {aircraft.map(plane => (
                                <option key={plane.id} value={plane.id}>{plane.make || plane.manufacturer} {plane.model} {plane.tailNumber || plane.registration}</option>
                              ))}
                            </select>
                          ) : (
                            expenseAircraft ? `${expenseAircraft.make || expenseAircraft.manufacturer || ''} ${expenseAircraft.model || ''} ${expenseAircraft.tailNumber || expenseAircraft.registration || ''}`.trim() : ''
                          )}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? <input type="number" step="0.01" value={expense.total || ''} onChange={(e) => updateUserExpenseField(expense.id, 'total', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${expense.total || 0}`}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? (
                            <>
                              <input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,.webp"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (file) handleUserExpenseReceiptChange(expense.id, file);
                                }}
                                style={{ width: '100%', border: 'none', fontSize: '12px' }}
                              />
                              <ReceiptExtractionStatus isLoading={extractingUserExpenseIds.includes(expense.id)} />
                            </>
                          ) : (
                            (expense.receiptImage || expense.isAttachment) ? (
                              <span 
                                onClick={async () => {
                                  try {
                                    const response = await fetch(`${API_BASE_URL}/expense/${expense.id}/image`, {
                                      headers: {
                                        'session-id': currentUser?.sessionId
                                      }
                                    });
                                    if (response.ok) {
                                      const blob = await response.blob();
                                      const imageUrl = URL.createObjectURL(blob);
                                      setSelectedReceiptImage(imageUrl);
                                      const isPdf = blob.type === 'application/pdf';
                                      setSelectedReceiptType(isPdf ? 'pdf' : 'image');
                                      setShowReceiptModal(true);
                                    }
                                  } catch (error) {
                                    console.error('Error loading receipt:', error);
                                  }
                                }}
                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                title="Click to view receipt"
                              >
                                📎
                              </span>
                            ) : ''
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {userExpenses.length === 0 && (
                    <tr>
                      <td colSpan={(!isReadOnly || allowProgressEdit) ? "5" : "4"} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No progress data found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {activeTab === 'progress' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0 }}>Training Progress</h4>
              <select
                value={progressCategoryFilter}
                onChange={(e) => selectProgressCategoryFilter(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '180px' }}
              >
                {progressCategories.length === 0 ? (
                  <option value="">No categories</option>
                ) : progressCategories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <select
                value={progressStageFilter}
                onChange={(e) => setProgressStageFilter(e.target.value)}
                disabled={!progressCategoryFilter || progressStages.length === 0}
                style={{ padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '180px', opacity: (!progressCategoryFilter || progressStages.length === 0) ? 0.6 : 1 }}
              >
                {progressStages.length === 0 ? (
                  <option value="">No stages</option>
                ) : progressStages.map((stage) => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
            </div>
            {progressMessage && (
              <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: progressMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: progressMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${progressMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                {progressMessage}
              </div>
            )}
            {progressData.length === 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                <tbody>
                  <tr>
                    <td colSpan={(!isReadOnly || allowProgressEdit) ? "6" : "5"} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No progress data found</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                      {(!isReadOnly || allowProgressEdit) && <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '48px' }}>Actions</th>}
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Requirement</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Category</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Stage</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Status</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProgressData.length === 0 ? (
                      <tr>
                        <td colSpan={(!isReadOnly || allowProgressEdit) ? "6" : "5"} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No progress data found for the selected filters</td>
                      </tr>
                    ) : filteredProgressData.map((item, index) => {
                      const level = parseInt(item.level);
                      const isEditing = editingProgressId === item.id;
                      const backgroundColor = level >= 4 ? '#e8f5e8' : level === 3 ? '#e8f4fd' : 'white';
                      return (
                        <tr key={item.id || index} style={{ backgroundColor }}>
                          {(!isReadOnly || allowProgressEdit) && (
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {isEditing ? (
                                  <>
                                    <button onClick={() => saveProgressItem(item)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                    <button onClick={() => setEditingProgressId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                  </>
                                ) : (
                                  <button onClick={() => setEditingProgressId(item.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                                )}
                              </div>
                            </td>
                          )}
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{item.requirement || ''}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{item.category || ''}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{item.stage || ''}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing && (!isReadOnly || allowProgressEdit) ? (
                              <select value={item.status || ''} onChange={(e) => updateProgressField(item.id, 'status', e.target.value)} style={{ width: '100%', border: 'none' }}>
                                <option value="">Not Started</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                              </select>
                            ) : (
                              <span style={{ 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '4px', 
                                fontSize: '0.7rem',
                                backgroundColor: item.status === 'completed' ? '#d4edda' : item.status === 'in_progress' ? '#fff3cd' : '#f8d7da',
                                color: item.status === 'completed' ? '#155724' : item.status === 'in_progress' ? '#856404' : '#721c24'
                              }}>
                                {item.status || 'Not Started'}
                              </span>
                            )}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing && (!isReadOnly || allowProgressEdit) ? (
                              <select value={item.level || ''} onChange={(e) => updateProgressField(item.id, 'level', e.target.value)} style={{ width: '100%', border: 'none' }}>
                                <option value="">None</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                                <option value="5">5</option>
                              </select>
                            ) : (
                              item.level || ''
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'documents' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4>Documents</h4>
              {!isReadOnly && <button onClick={addNewDocument} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add New</button>}
            </div>
            {documentMessage && (
              <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: documentMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: documentMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${documentMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                {documentMessage}
              </div>
            )}
            <div style={{ overflow: 'auto', maxHeight: '400px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                    {!isReadOnly && <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Actions</th>}
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Document Type</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Description</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Document Date</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Expiration Date</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Document</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((document, index) => {
                    const isEditing = editingDocumentId === document.id;
                    return (
                      <tr key={document.id || index}>
                        {!isReadOnly && (
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              {isEditing ? (
                                <>
                                  <button onClick={() => saveDocument(document)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                  <button onClick={() => setEditingDocumentId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                </>
                              ) : (
                                <button onClick={() => setEditingDocumentId(document.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                              )}
                            </div>
                          </td>
                        )}
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? (
                            <select value={document.documentType || ''} onChange={(e) => updateDocumentField(document.id, 'documentType', e.target.value)} style={{ width: '100%', border: 'none' }}>
                              <option value="">Select Type</option>
                              <option value="pilot certificate">pilot certificate</option>
                              <option value="medical certificate">medical certificate</option>
                              <option value="drivers license">drivers license</option>
                              <option value="passport">passport</option>
                              <option value="insurance">insurance</option>
                              <option value="endorsement">endorsement</option>
                              <option value="other">other</option>
                            </select>
                          ) : (
                            document.documentType || ''
                          )}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? <input type="text" value={document.description || ''} onChange={(e) => updateDocumentField(document.id, 'description', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (document.description || '')}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? <input type="date" value={document.documentDate || ''} onChange={(e) => updateDocumentField(document.id, 'documentDate', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (document.documentDate ? new Date(document.documentDate + 'T00:00:00').toLocaleDateString() : '')}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? <input type="date" value={document.expirationDate || ''} onChange={(e) => updateDocumentField(document.id, 'expirationDate', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (document.expirationDate ? new Date(document.expirationDate + 'T00:00:00').toLocaleDateString() : '')}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {isEditing ? (
                            <input type="file" accept="image/*" onChange={(e) => updateDocumentField(document.id, 'documentFile', e.target.files[0])} style={{ width: '100%', border: 'none', fontSize: '12px' }} />
                          ) : (
                            <span 
                              onClick={async () => {
                                try {
                                  const response = await fetch(`${API_BASE_URL}/document/${document.id}/image`, {
                                    headers: { 'session-id': currentUser?.sessionId }
                                  });
                                  if (response.ok) {
                                    const blob = await response.blob();
                                    const imageUrl = URL.createObjectURL(blob);
                                    setSelectedDocumentImage(imageUrl);
                                    setShowDocumentModal(true);
                                  }
                                } catch (error) {
                                  console.error('Error loading document image:', error);
                                }
                              }}
                              style={{ cursor: 'pointer', fontSize: '16px' }}
                              title="Click to view document"
                            >
                              📄
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {documents.length === 0 && (
                    <tr>
                      <td colSpan={!isReadOnly ? "6" : "5"} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No documents found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {activeTab === 'reservations' && (
          <div>
            <h4>Reservations</h4>
            {userReservations.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666' }}>No reservations found</div>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                <table style={{ minWidth: '1200px', width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', minWidth: '112px' }}>Time</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Aircraft</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Instructor</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Client</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Type</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Status</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Comment</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Maneuvers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userReservations.map((reservation, index) => (
                      <tr key={reservation.id || index}>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {reservation.startDate ? new Date(reservation.startDate.split('T')[0] + 'T00:00:00').toLocaleDateString() : ''}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {formatTime(reservation.startTime)} - {formatTime(reservation.endTime)}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {reservation.aircraft ? `${reservation.aircraft.make} ${reservation.aircraft.model} ${reservation.aircraft.tailNumber}` : ''}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {reservation.instructor ? `${reservation.instructor.firstName} ${reservation.instructor.lastName}` : ''}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {reservation.client ? `${reservation.client.firstName} ${reservation.client.lastName}` : ''}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.7rem',
                            backgroundColor: reservation.type === 'lesson' ? '#e3f2fd' : reservation.type === 'solo' ? '#f3e5f5' : '#fff3e0',
                            color: reservation.type === 'lesson' ? '#1565c0' : reservation.type === 'solo' ? '#7b1fa2' : '#ef6c00'
                          }}>
                            {reservation.type || 'N/A'}
                          </span>
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.7rem',
                            backgroundColor: reservation.status === 'active' ? '#d4edda' : reservation.status === 'completed' ? '#cce5ff' : '#f8d7da',
                            color: reservation.status === 'active' ? '#155724' : reservation.status === 'completed' ? '#004085' : '#721c24'
                          }}>
                            {reservation.status || 'N/A'}
                          </span>
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', maxWidth: '200px', wordWrap: 'break-word' }}>
                          {reservation.comment || ''}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem', maxWidth: '200px', wordWrap: 'break-word' }}>
                          {reservation.maneuvers && reservation.maneuvers.length > 0 ? 
                            reservation.maneuvers.map(m => m.name).join(', ') : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {activeTab === 'popupQuestions' && canViewPopupQuestionHistory && (
          <div>
            <h4 style={{ margin: '0 0 1rem', color: '#1c4b5d' }}>Answered Practice Questions</h4>
            {popupQuestionAnswersMessage && (
              <div role="status" style={{ padding: '0.65rem 0.8rem', marginBottom: '1rem', color: popupQuestionAnswersMessage.includes('saved') ? '#155724' : '#721c24', backgroundColor: popupQuestionAnswersMessage.includes('saved') ? '#d4edda' : '#f8d7da', borderRadius: '5px' }}>
                {popupQuestionAnswersMessage}
              </div>
            )}
            {popupQuestionAnswersLoading ? (
              <p style={{ color: '#687982' }}>Loading answered questions…</p>
            ) : popupQuestionAnswers.length === 0 ? (
              <p style={{ padding: '1.5rem 0', color: '#687982' }}>No answered questions yet.</p>
            ) : (
              <div>
                {popupQuestionAnswers.map((questionAnswer) => {
                  const answerLabel = questionAnswer.answerType === 'multiple_choice'
                    ? questionAnswer[`answerChoice${questionAnswer.userAnswer}`] || `Choice ${questionAnswer.userAnswer || ''}`
                    : questionAnswer.userAnswer || 'No answer submitted';
                  const correctAnswer = questionAnswer.correctAnswer
                    || (questionAnswer.correctChoice ? questionAnswer[`answerChoice${questionAnswer.correctChoice}`] : '');
                  const feedbackDraft = popupQuestionFeedbackDrafts[questionAnswer.id] ?? questionAnswer.instructorFeedback ?? '';

                  return (
                    <section key={questionAnswer.id} style={{ padding: '1rem 0', borderTop: '1px solid #d4e1e6' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.6rem', color: '#60737d', fontSize: '0.8rem' }}>
                        <span>{formatTrainingLevel(questionAnswer.trainingLevel)} · {questionAnswer.questionStyle?.replaceAll('_', ' ')}</span>
                        <time>{questionAnswer.answeredDate ? new Date(questionAnswer.answeredDate).toLocaleString() : ''}</time>
                      </div>
                      <p style={{ margin: '0 0 0.75rem', color: '#263943', fontWeight: 600, lineHeight: 1.5 }}>{questionAnswer.question}</p>
                      <p style={{ margin: '0 0 0.5rem', lineHeight: 1.5 }}><strong>Answer:</strong> {answerLabel}</p>
                      <div style={{ margin: '0.65rem 0', padding: '0.7rem 0.8rem', backgroundColor: questionAnswer.isCorrect === true ? '#edf7ef' : questionAnswer.isCorrect === false ? '#fbf0f0' : '#f3f6f7', color: questionAnswer.isCorrect === true ? '#285b3e' : questionAnswer.isCorrect === false ? '#842029' : '#526671', borderRadius: '5px' }}>
                        <strong>{questionAnswer.isCorrect === true ? 'Correct' : questionAnswer.isCorrect === false ? 'Not quite' : 'Not graded'}</strong>
                        {correctAnswer && <p style={{ margin: '0.4rem 0 0', lineHeight: 1.5 }}><strong>Correct answer:</strong> {correctAnswer}</p>}
                        {questionAnswer.answerFeedback && <p style={{ margin: '0.4rem 0 0', lineHeight: 1.5 }}>{questionAnswer.answerFeedback}</p>}
                        {questionAnswer.evaluationSource && <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem' }}>Evaluation: {questionAnswer.evaluationSource === 'answer_key' ? 'Answer key' : 'AI practice evaluation'}</p>}
                      </div>
                      {canReviewPopupQuestionAnswers && (
                        <div style={{ margin: '0.75rem 0', padding: '0.75rem', backgroundColor: '#f5f8f9', borderLeft: '3px solid #4a899b' }}>
                          {correctAnswer && <p style={{ margin: '0 0 0.4rem' }}><strong>AI reference choice:</strong> {correctAnswer}</p>}
                          <p style={{ margin: 0, lineHeight: 1.5 }}><strong>AI reference answer:</strong> {questionAnswer.referenceAnswer}</p>
                        </div>
                      )}
                      {questionAnswer.instructorFeedback && (
                        <p style={{ margin: '0.75rem 0', padding: '0.65rem 0.75rem', backgroundColor: '#f0f7f3', color: '#285b3e', lineHeight: 1.5 }}>
                          <strong>Instructor feedback:</strong> {questionAnswer.instructorFeedback}
                        </p>
                      )}
                      {canReviewPopupQuestionAnswers && (
                        <div style={{ marginTop: '0.75rem' }}>
                          <label htmlFor={`popup-question-feedback-${questionAnswer.id}`} style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.9rem', fontWeight: 600 }}>Instructor feedback</label>
                          <textarea
                            id={`popup-question-feedback-${questionAnswer.id}`}
                            value={feedbackDraft}
                            maxLength={10000}
                            rows="3"
                            onChange={(event) => setPopupQuestionFeedbackDrafts((previous) => ({ ...previous, [questionAnswer.id]: event.target.value }))}
                            style={{ width: '100%', padding: '0.65rem', border: '1px solid #cbd6dc', borderRadius: '5px', boxSizing: 'border-box', font: 'inherit', resize: 'vertical' }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                            <button
                              type="button"
                              onClick={() => savePopupQuestionFeedback(questionAnswer)}
                              disabled={!feedbackDraft.trim() || savingPopupQuestionFeedbackId != null}
                              style={{ padding: '0.5rem 0.85rem', color: 'white', backgroundColor: !feedbackDraft.trim() || savingPopupQuestionFeedbackId != null ? '#8499a1' : '#176b85', border: 0, borderRadius: '5px', cursor: !feedbackDraft.trim() || savingPopupQuestionFeedbackId != null ? 'not-allowed' : 'pointer' }}
                            >{savingPopupQuestionFeedbackId === questionAnswer.id ? 'Saving…' : 'Save feedback'}</button>
                          </div>
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'flightLog' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4>Flight Log</h4>
              <button onClick={() => { setIsAddingFlightLog(true); setNewFlightLogData({ userId: userData?.id, flightDate: '', isSummary: false }); }} disabled={isAddingFlightLog} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: isAddingFlightLog ? 'not-allowed' : 'pointer', opacity: isAddingFlightLog ? 0.6 : 1 }}>Add Entry</button>
            </div>
            {flightLogMessage && (
              <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: flightLogMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: flightLogMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${flightLogMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>{flightLogMessage}</div>
            )}
            <div style={{ overflow: 'auto', maxHeight: '380px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.75rem', minWidth: '1400px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'left', width: '70px' }}>Actions</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'left', width: '90px' }}>Date</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'left' }}>Make</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'left' }}>Model</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'left' }}>From</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'left' }}>Route</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'left' }}>To</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>ASEL</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>AMEL</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>Dual Rcvd</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>PIC</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>Dual Given</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>Night</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>XC</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>Act Inst</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>Sim Inst</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>Appr</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>Day Ldg</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>Night Ldg</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>Total</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'left' }}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {isAddingFlightLog && (
                    <tr style={{ backgroundColor: '#e8f5e8' }}>
                      <td style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button onClick={() => saveFlightLogEntry(true)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px', marginRight: '2px' }}>💾</button>
                        <button onClick={() => { setIsAddingFlightLog(false); setNewFlightLogData({}); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px' }}>❌</button>
                      </td>
                      {['flightDate','aircraftMake','aircraftModel','origin','route','destination'].map(f => (
                        <td key={f} style={{ border: '1px solid #ddd', padding: '0.4rem' }}><input type={f === 'flightDate' ? 'date' : 'text'} value={newFlightLogData[f] || ''} onChange={e => setNewFlightLogData({...newFlightLogData, [f]: e.target.value})} style={{ width: '100%', border: 'none', fontSize: '0.75rem' }} /></td>
                      ))}
                      {['asel','amel','dualReceived','pic','dualGiven','night','crossCountry','actualInstrument','simulatedInstrument','approaches','landingsDay','landingsNight','totalFlightTime'].map(f => (
                        <td key={f} style={{ border: '1px solid #ddd', padding: '0.4rem' }}><input type="number" step={['approaches','landingsDay','landingsNight'].includes(f) ? '1' : '0.1'} value={newFlightLogData[f] || ''} onChange={e => setNewFlightLogData({...newFlightLogData, [f]: e.target.value})} style={{ width: '50px', border: 'none', fontSize: '0.75rem' }} /></td>
                      ))}
                      <td style={{ border: '1px solid #ddd', padding: '0.4rem' }}><input type="text" value={newFlightLogData.remarks || ''} onChange={e => setNewFlightLogData({...newFlightLogData, remarks: e.target.value})} style={{ width: '100%', border: 'none', fontSize: '0.75rem' }} /></td>
                    </tr>
                  )}
                  {flightLog.length === 0 && !isAddingFlightLog ? (
                    <tr><td colSpan="21" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No flight log entries found</td></tr>
                  ) : (
                    flightLog.map((entry, index) => {
                      const isEditing = editingFlightLogId === entry.id;
                      const bg = entry.isSummary ? '#fff9e6' : index % 2 === 0 ? 'white' : '#f8f9fa';
                      return (
                        <tr key={entry.id} style={{ backgroundColor: bg }}>
                          <td style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            <button onClick={isEditing ? () => saveFlightLogEntry(false) : () => { setEditingFlightLogId(entry.id); setEditingFlightLogData({...entry}); }} disabled={isAddingFlightLog || (editingFlightLogId && editingFlightLogId !== entry.id)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px', marginRight: '2px', opacity: (isAddingFlightLog || (editingFlightLogId && editingFlightLogId !== entry.id)) ? 0.4 : 1 }}>{isEditing ? '💾' : '✏️'}</button>
                            {isEditing ? (
                              <button onClick={() => { setEditingFlightLogId(null); setEditingFlightLogData({}); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px' }}>❌</button>
                            ) : (
                              <button onClick={() => { setDeleteFlightLogId(entry.id); setShowDeleteFlightLogModal(true); }} disabled={isAddingFlightLog || editingFlightLogId} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px', opacity: (isAddingFlightLog || editingFlightLogId) ? 0.4 : 1 }}>🗑️</button>
                            )}
                          </td>
                          {['flightDate','aircraftMake','aircraftModel','origin','route','destination'].map(f => (
                            <td key={f} style={{ border: '1px solid #ddd', padding: '0.4rem' }}>{isEditing ? <input type={f === 'flightDate' ? 'date' : 'text'} value={editingFlightLogData[f] || ''} onChange={e => setEditingFlightLogData({...editingFlightLogData, [f]: e.target.value})} style={{ width: '100%', border: 'none', fontSize: '0.75rem' }} /> : (f === 'flightDate' && entry[f] ? entry[f].split('T')[0] : entry[f] || '')}</td>
                          ))}
                          {['asel','amel','dualReceived','pic','dualGiven','night','crossCountry','actualInstrument','simulatedInstrument','approaches','landingsDay','landingsNight','totalFlightTime'].map(f => (
                            <td key={f} style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>{isEditing ? <input type="number" step={['approaches','landingsDay','landingsNight'].includes(f) ? '1' : '0.1'} value={editingFlightLogData[f] ?? ''} onChange={e => setEditingFlightLogData({...editingFlightLogData, [f]: e.target.value})} style={{ width: '50px', border: 'none', fontSize: '0.75rem' }} /> : (entry[f] ?? '')}</td>
                          ))}
                          <td style={{ border: '1px solid #ddd', padding: '0.4rem' }}>{isEditing ? <input type="text" value={editingFlightLogData.remarks || ''} onChange={e => setEditingFlightLogData({...editingFlightLogData, remarks: e.target.value})} style={{ width: '100%', border: 'none', fontSize: '0.75rem' }} /> : (entry.remarks || '')}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {flightLog.length > 0 && (
                  <tfoot>
                    <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold', fontSize: '0.75rem' }}>
                      <td colSpan="7" style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'right' }}>Totals:</td>
                      {['asel','amel','dualReceived','pic','dualGiven','night','crossCountry','actualInstrument','simulatedInstrument','approaches','landingsDay','landingsNight','totalFlightTime'].map(f => (
                        <td key={f} style={{ border: '1px solid #ddd', padding: '0.4rem', textAlign: 'center' }}>
                          {['approaches','landingsDay','landingsNight'].includes(f)
                            ? flightLog.reduce((s, e) => s + (parseInt(e[f]) || 0), 0)
                            : flightLog.reduce((s, e) => s + (parseFloat(e[f]) || 0), 0).toFixed(1)}
                        </td>
                      ))}
                      <td style={{ border: '1px solid #ddd', padding: '0.4rem' }}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {activeTab === 'agreements' && (
          <div>
            <h4>Agreements</h4>
            <div style={{ overflow: 'auto', maxHeight: '400px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Agreement</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Signed</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Viewed</th>
                    <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Signed Date</th>
                  </tr>
                </thead>
                <tbody>
                  {userAgreements.length === 0 ? (
                    <tr><td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No agreements found</td></tr>
                  ) : (
                    userAgreements.map((agreement) => (
                      <tr key={agreement.id}>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{agreement.name || ''}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', backgroundColor: agreement.signed ? '#d4edda' : '#f8d7da', color: agreement.signed ? '#155724' : '#721c24' }}>
                            {agreement.signed ? 'Signed' : 'Not Signed'}
                          </span>
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {agreement.viewedDatetime ? new Date(agreement.viewedDatetime).toLocaleString() : '—'}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {agreement.signedDatetime ? new Date(agreement.signedDatetime).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#e9ecef' }}>
        {!isReadOnly && onSave && <button type="button" onClick={handleDialogSave} disabled={instructorWebsiteSaving || (showInstructorWebsiteTab && instructorWebsiteLoading)} style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: instructorWebsiteSaving ? 'wait' : 'pointer', opacity: instructorWebsiteSaving ? 0.7 : 1 }}>{instructorWebsiteSaving ? 'Saving...' : 'Save'}</button>}
        <button type="button" onClick={() => {
          // Clear all user-specific data
          setPersonLessons([]);
          setWorkLessons([]);
          setExpenses([]);
          setEarningsStatements([]);
          setUserExpenses([]);
          setDocuments([]);
          setProgressData([]);
          setUserReservations([]);
          setWeeklySchedule({
            sunday: { startTime: '', endTime: '' },
            monday: { startTime: '', endTime: '' },
            tuesday: { startTime: '', endTime: '' },
            wednesday: { startTime: '', endTime: '' },
            thursday: { startTime: '', endTime: '' },
            friday: { startTime: '', endTime: '' },
            saturday: { startTime: '', endTime: '' }
          });
          setOverrides([]);
          setActiveTab('basic');
          onClose();
        }} style={{ marginLeft: (!isReadOnly && onSave) ? '1rem' : '0' }}>
          {isReadOnly ? 'Close' : 'Cancel'}
        </button>
      </div>
      
      {/* Earnings Statement Details Modal */}
      {showEarningsDetailsDialog && selectedEarningsStatement && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '900px', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>Earnings Statement Details ({selectedEarningsStatement.startDate} - {selectedEarningsStatement.endDate})</h3>
              <button onClick={() => setShowEarningsDetailsDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ padding: '1rem', backgroundColor: '#e3f2fd', borderBottom: '2px solid #2196f3' }}>
              <div style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                <div>Available Rental Hours: {availableRentalHours || 0}</div>
                <div>Rental Hours Earned: {selectedEarningsStatement.rentalHoursEarned || 0}</div>
                <div>Rental Hours Used: {selectedEarningsStatement.rentalHoursUsed || 0}</div>
              </div>
            </div>
            
            <div style={{ display: 'flex', backgroundColor: '#f8f9fa', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
              <button type="button" onClick={() => setEarningsDetailsActiveTab('workLessons')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: earningsDetailsActiveTab === 'workLessons' ? '#ffe6f0' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: earningsDetailsActiveTab === 'workLessons' ? '1px solid #ffe6f0' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: earningsDetailsActiveTab === 'workLessons' ? 'bold' : 'normal',
                marginRight: '2px',
                fontSize: '0.8rem'
              }}>Work Lessons</button>
              <button type="button" onClick={() => setEarningsDetailsActiveTab('expenses')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: earningsDetailsActiveTab === 'expenses' ? '#fff0e6' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: earningsDetailsActiveTab === 'expenses' ? '1px solid #fff0e6' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: earningsDetailsActiveTab === 'expenses' ? 'bold' : 'normal',
                fontSize: '0.8rem'
              }}>Expenses</button>
            </div>
            
            <div style={{ height: '450px', overflow: 'auto', backgroundColor: earningsDetailsActiveTab === 'workLessons' ? '#ffe6f0' : '#fff0e6', border: '1px solid #ccc', borderTop: 'none', padding: '0' }}>
              {earningsDetailsActiveTab === 'workLessons' && (
                <div style={{ height: '100%', overflow: 'auto' }}>
                  <div style={{ overflow: 'auto', height: '100%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Start Time</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Client Name</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Dual Given</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Ground Instruction</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {earningsWorkLessons.map((lesson, index) => (
                          <tr key={lesson.id || index}>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.startTime || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.clientName || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.dualGiven || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.groundInstruction || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.total || ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                          <td colSpan="3" style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'right' }}>Totals:</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{earningsWorkLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.dualGiven) || 0), 0).toFixed(1)}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{earningsWorkLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.groundInstruction) || 0), 0).toFixed(1)}</td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'center' }}>{earningsWorkLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.total) || 0), 0).toFixed(1)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
              
              {earningsDetailsActiveTab === 'expenses' && (
                <div style={{ height: '100%', overflow: 'auto' }}>
                  <div style={{ overflow: 'auto', height: '100%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Expense</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Vendor</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Aircraft</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Receipt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {earningsExpenses.map((expense, index) => {
                          const expenseAircraft = aircraft.find(a => a.id === expense.aircraftId);
                          return (
                            <tr key={expense.id || index}>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {expense.date ? new Date(expense.date + 'T00:00:00').toLocaleDateString() : ''}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {expense.expense || ''}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {expense.vendor || ''}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {expenseAircraft ? `${expenseAircraft.make || expenseAircraft.manufacturer || ''} ${expenseAircraft.model || ''} ${expenseAircraft.tailNumber || expenseAircraft.registration || ''}`.trim() : ''}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                ${expense.total || 0}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {expense.isAttachment ? (
                                  <span 
                                    onClick={async () => {
                                      try {
                                        const response = await fetch(`${API_BASE_URL}/expense/${expense.id}/image`, {
                                          headers: {
                                            'session-id': currentUser?.sessionId
                                          }
                                        });
                                        if (response.ok) {
                                          const blob = await response.blob();
                                          const imageUrl = URL.createObjectURL(blob);
                                          setSelectedReceiptImage(imageUrl);
                                          const isPdf = blob.type === 'application/pdf';
                                          setSelectedReceiptType(isPdf ? 'pdf' : 'image');
                                          setShowReceiptModal(true);
                                        }
                                      } catch (error) {
                                        console.error('Error loading receipt image:', error);
                                      }
                                    }}
                                    style={{ cursor: 'pointer', fontSize: '16px' }}
                                    title="Click to view receipt"
                                  >
                                    📎
                                  </span>
                                ) : ''}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowEarningsDetailsDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Receipt Modal */}
      {showReceiptModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: selectedReceiptType === 'pdf' ? '90vw' : 'auto', height: selectedReceiptType === 'pdf' ? '90vh' : 'auto', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Receipt</h3>
              <button onClick={() => setShowReceiptModal(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', textAlign: 'center' }}>
              {selectedReceiptType === 'pdf' ? (
                <iframe src={selectedReceiptImage} style={{ width: '100%', height: '100%', border: 'none', minHeight: '75vh' }} title="Receipt PDF" />
              ) : (
                <img src={selectedReceiptImage} alt="Receipt" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Document Modal */}
      {showDocumentModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Document Image</h3>
              <button onClick={() => setShowDocumentModal(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', textAlign: 'center' }}>
              <img src={selectedDocumentImage} alt="Document" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            </div>
          </div>
        </div>
      )}
      
      {/* Invoice Modal */}
      {showInvoiceModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '90vw', height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Invoice</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={async () => {
                  if (!currentInvoiceLessonId) return;
                  setIsSendingEmail(true);
                  try {
                    const response = await fetch(`${API_BASE_URL}/lesson/${currentInvoiceLessonId}/invoice/email`, {
                      method: 'POST',
                      headers: { 'session-id': currentUser?.sessionId }
                    });
                    if (response.ok) {
                      alert('Invoice sent successfully');
                    } else {
                      const errorText = await response.text();
                      alert(`Failed to send invoice: ${errorText}`);
                    }
                  } catch (error) {
                    alert(`Error: ${error.message}`);
                  } finally {
                    setIsSendingEmail(false);
                  }
                }} disabled={isSendingEmail} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: isSendingEmail ? 'not-allowed' : 'pointer', opacity: isSendingEmail ? 0.6 : 1 }}>{isSendingEmail ? '📧 Sending...' : '📧 Email'}</button>
                <button onClick={() => {
                  const link = document.createElement('a');
                  link.href = invoicePdfUrl;
                  link.download = 'invoice.pdf';
                  link.click();
                }} style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>📥 Download</button>
                <button onClick={() => {
                  setShowInvoiceModal(false);
                  URL.revokeObjectURL(invoicePdfUrl);
                  setInvoicePdfUrl('');
                  setCurrentInvoiceLessonId(null);
                }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <iframe src={invoicePdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Invoice PDF" />
            </div>
          </div>
        </div>
      )}
      
      {isLoadingInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '6px solid #f3f3f3',
            borderTop: '6px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        </div>
      )}
    </div>

      {showDeleteFlightLogModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', maxWidth: '380px', width: '90%' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Confirm Delete</h3>
            <p style={{ margin: '0 0 2rem 0', textAlign: 'center', color: '#555' }}>Are you sure you want to delete this flight log entry?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={() => { deleteFlightLogEntry(deleteFlightLogId); setShowDeleteFlightLogModal(false); setDeleteFlightLogId(null); }} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Yes, Delete</button>
              <button onClick={() => { setShowDeleteFlightLogModal(false); setDeleteFlightLogId(null); }} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserDialog;
